import { useState, useEffect, useCallback, ChangeEvent, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { Layout, Row, Col, Statistic, Card, Steps, Button, Space, message, Form, Input, Modal, Select, InputNumber, Tag, AutoComplete, Tooltip, Checkbox, Dropdown, MenuProps, Spin, Typography, Segmented } from 'antd';
import { EyeOutlined, TagOutlined, DollarCircleOutlined, UserOutlined, MailOutlined, PhoneOutlined, FileTextOutlined, TeamOutlined, IdcardOutlined, DeleteOutlined, DownOutlined, DashboardOutlined, UnorderedListOutlined, CheckSquareOutlined, CalendarOutlined, SearchOutlined, SettingOutlined, MenuFoldOutlined, MenuUnfoldOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  api,
  setTokens,
  getAccessToken,
  refreshAccessToken,
  clearTokens,
  setUnauthorizedHandler,
  setTokenListener,
  fetchPipelineHistory,
  createCustomerDraftFromLead
} from './api';
import {
  formatAmount,
  statusDefinitions,
  statusLabelMap,
  statusTagColors,
  currencyOptions,
  languageOptions,
  formatCvrNumber,
  formatDateTimeShort,
  formatDateTimeFull,
  leadTierDefinitions,
  leadTierDefinitionMap,
  leadTierTagColors,
  deriveLeadTier
} from './constants';
import type { LeadTier } from './constants';
import AdminReferrersPage from './AdminReferrersPage';
import AdminGroupsPage from './AdminGroupsPage';
import AdminUsersPage from './AdminUsersPage';
import AdminDatafordelerEventsPage from './AdminDatafordelerEventsPage';
import DocumentLearningPage from './DocumentLearningPage';
import CompanySearchPage from './CompanySearchPage';
import WorkListsManagementPage from './WorkListsManagementPage';
import CustomersPage from './CustomersPage';
import LeadDetailPage from './LeadDetailPage';
import LeadTodosPage from './LeadTodosPage';
import LeadGroupsPage from './LeadGroupsPage';
import CustomizationPage from './CustomizationPage';
import CalendarPage from './CalendarPage';
import ChangeRequestsPage from './ChangeRequestsPage';
import DashboardPage from './DashboardPage';
import InsuranceTypesPage from './InsuranceTypesPage';
import InsuranceConditionsPage from './InsuranceConditionsPage';
import TenderTemplatesPage from './TenderTemplatesPage';
import { AgreementModal } from './components/AgreementModal';
import { PipelineActionButtons } from './components/PipelineControls';
import {
  CalendarEvent,
  Claims,
  CvrSummary,
  CompanyLookupResult,
  Label,
  LeadTodo,
  LeadTodoStatus,
  PipelineHistoryResponse,
  InsuranceType,
  CustomerInsuranceType
} from './types';
import { transitionOptions } from './leadTransitions';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { fetchAllLeadTodos } from './leadTodosApi';
import './App.css';

const { Text } = Typography;

const ACCESS_TOKEN_STORAGE_KEY = 'maegler_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'maegler_refresh_token';
const DASHBOARD_INCLUDE_HIDDEN_RECENT_LEADS_STORAGE_KEY = 'maegler_dashboard_include_hidden_recent_leads';

type ActiveView =
  | 'dashboard'
  | 'leads'
  | 'todos'
  | 'calendar'
  | 'referrers'
  | 'customers'
  | 'companies'
  | 'workLists'
  | 'leadGroups'
  | 'groups'
  | 'users'
  | 'datafordelerEvents'
  | 'documentLearning'
  | 'customization'
  | 'changeRequests'
  | 'insuranceTypes'
  | 'insuranceConditions'
  | 'tenderTemplates';

type RawCalendarEvent = {
  id: string;
  title?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  notes?: string | null;
  lead_id?: string | null;
  lead_company_name?: string | null;
  source?: string | null;
  created_at?: string | null;
  created_by?: string | null;
};

function mapRawCalendarEvent(raw: RawCalendarEvent): CalendarEvent {
  const normalizedSource: CalendarEvent['source'] =
    raw.source === 'LEAD_MEETING' ? 'LEAD_MEETING' : 'CALENDAR';
  return {
    id: raw.id,
    title: raw.title ?? raw.lead_company_name ?? null,
    startAt: raw.start_at ?? null,
    endAt: raw.end_at ?? null,
    location: raw.location ?? null,
    notes: raw.notes ?? null,
    leadId: raw.lead_id ?? null,
    leadCompanyName: raw.lead_company_name ?? null,
    source: normalizedSource,
    createdAt: raw.created_at ?? null,
    createdBy: raw.created_by ?? null
  };
}

const UPCOMING_MEETING_WINDOW_DAYS = 3;
const UPCOMING_TODO_WINDOW_DAYS = 5;
const DASHBOARD_SECTION_LIMIT = 5;

function decodeClaims(token: string): Claims | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const json = atob(padded);
    const parsed = JSON.parse(json);
    if (!parsed?.role) return null;
    return {
      sub: parsed.sub,
      email: typeof parsed.email === 'string' ? parsed.email : parsed.sub,
      role: parsed.role,
      referrer_id: parsed.referrer_id
    };
  } catch (error) {
    console.error('Failed to decode token', error);
    return null;
  }
}

function readTokenExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const json = atob(padded);
    const parsed = JSON.parse(json);
    return typeof parsed?.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string) {
  const exp = readTokenExpiry(token);
  if (!exp) {
    return true;
  }
  return exp * 1000 <= Date.now();
}

function validateOptionalCvr(_: unknown, value: string | undefined) {
  if (value === undefined || value === null || value === '') {
    return Promise.resolve();
  }
  const digits = `${value}`.replace(/\D+/g, '');
  if (!digits) {
    return Promise.resolve();
  }
  if (digits.length === 8) {
    return Promise.resolve();
  }
  return Promise.reject(new Error('CVR-nummer skal bestå af 8 cifre'));
}

function LeadCard({
  lead,
  refresh,
  canManage,
  onEditFinancials,
  onOpenDetail,
  onAgreementRequest,
  availableLabels,
  onAttachLabels
}: {
  lead: any;
  refresh: () => void | Promise<void>;
  canManage: boolean;
  onEditFinancials?: (lead: any) => void;
  onOpenDetail?: (leadId: string) => void;
  onAgreementRequest?: (leadId: string) => void;
  availableLabels?: Label[];
  onAttachLabels?: (leadId: string, labelIds: string[]) => Promise<void>;
}) {
  const normalizedStatus = lead.status === 'REFERRED' ? 'NOT_CONTACTED' : lead.status;
  const statusLabel = statusLabelMap.get(normalizedStatus) ?? normalizedStatus;
  const currentIndex = Math.max(
    0,
    statusDefinitions.findIndex((item) => item.value === normalizedStatus)
  );
  const stepItems = statusDefinitions.map((item) => ({ key: item.value, title: item.label }));
  const actions = canManage
    ? transitionOptions[normalizedStatus as keyof typeof transitionOptions] ?? []
    : [];
  const cancelLabel = 'Ingen kontrakt';
  const hasReferrerDetails = Boolean(
    canManage && (lead.ref_full_name || lead.ref_email || lead.ref_phone)
  );
  const formattedCvr = formatCvrNumber(lead.cvrnummer);
  const employeesRaw = lead.cvr_ansatte_aar ?? lead.ansatte_aar;
  const employeesCount = employeesRaw === undefined || employeesRaw === null ? null : Number(employeesRaw);
  const employeesDisplay =
    employeesCount === null || Number.isNaN(employeesCount) ? employeesRaw ?? null : employeesCount;
  const employeesDateRaw = lead.cvr_ansatte_datotil ?? lead.ansatte_datotil;
  const employeesYear = (() => {
    if (!employeesDateRaw) return null;
    const parsed = new Date(employeesDateRaw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getFullYear().toString();
    }
    const yearCandidate = String(employeesDateRaw).slice(0, 4);
    return yearCandidate.length === 4 ? yearCandidate : String(employeesDateRaw);
  })();
  const hasCvr = Boolean(formattedCvr);
  const hasDetails = Boolean(
    hasCvr ||
    lead.contact_name ||
    lead.contact_email ||
    lead.contact_phone ||
    employeesDisplay !== null ||
    lead.notes ||
    hasReferrerDetails
  );
  const financialReady = lead.contract_value_annual !== null && lead.contract_value_annual !== undefined;
  const leadLabels: Array<{ id: string; name: string; color?: string }> = Array.isArray(lead.labels)
    ? lead.labels
    : [];
  const callAttemptCount = Number(lead.call_attempt_count ?? 0);
  const isHidden = Boolean(lead.hidden_at);
  const lastCallShort = formatDateTimeShort(lead.last_call_at ?? null);
  const lastCallFull = formatDateTimeFull(lead.last_call_at ?? null);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [callAttemptLoading, setCallAttemptLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [attachingLabels, setAttachingLabels] = useState(false);
  const assignableLabels = (availableLabels || []).filter(
    (label) => !leadLabels.some((assigned) => assigned.id === label.id)
  );

  const attachLabels = useCallback(async () => {
    if (!onAttachLabels || selectedLabelIds.length === 0) {
      return;
    }
    setAttachingLabels(true);
    try {
      await onAttachLabels(lead.id, selectedLabelIds);
      setSelectedLabelIds([]);
    } finally {
      setAttachingLabels(false);
    }
  }, [lead.id, onAttachLabels, selectedLabelIds]);
  const performStatusUpdate = useCallback(
    async (nextStatus: string, extraPayload?: Record<string, unknown>) => {
      if (nextStatus === lead.status) {
        return true;
      }
      setStatusUpdating(true);
      try {
        const res = await api(`/api/leads/${lead.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toStatus: nextStatus, ...extraPayload })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          message.error(err.error || 'Kunne ikke opdatere status');
          return false;
        }
        message.success('Status opdateret');
        await refresh();
        return true;
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere status');
        return false;
      } finally {
        setStatusUpdating(false);
      }
    },
    [lead.id, lead.status, refresh]
  );

  async function handleTransition(toStatus: string) {
    if (toStatus === 'AGREEMENT_MADE') {
      onAgreementRequest?.(lead.id);
      return;
    }

    await performStatusUpdate(toStatus);
  }


  const tagColor = statusTagColors[normalizedStatus] ?? 'default';
  const leadTier = deriveLeadTier(normalizedStatus);
  const tierMeta = leadTierDefinitionMap.get(leadTier);
  const tierLabel =
    tierMeta?.label ??
    (leadTier === 'CUSTOMER'
      ? 'Kunde'
      : leadTier === 'IN_PROGRESS'
        ? 'Sag'
        : 'Lead');
  const tierDescription = tierMeta?.description;
  const tierColor = leadTierTagColors[leadTier] || 'default';
  const itemLabel = leadTier === 'IN_PROGRESS' ? 'sag' : leadTier === 'CUSTOMER' ? 'kunde' : 'lead';
  const tenderTemplateTitle = lead.last_tender_template_title;
  const tenderTemplateBody = lead.last_tender_template_body;
  const tenderNote = lead.last_tender_note;
  let tenderHistoryRaw: any = lead.tender_history_entries ?? lead.tender_history ?? [];
  if (typeof tenderHistoryRaw === 'string') {
    try {
      tenderHistoryRaw = JSON.parse(tenderHistoryRaw);
    } catch (error) {
      tenderHistoryRaw = [];
    }
  }
  if (!Array.isArray(tenderHistoryRaw)) {
    tenderHistoryRaw = [];
  }
  const tenderHistoryEntries = (tenderHistoryRaw as any[])
    .map((entry) => ({
      templateId:
        entry?.tender_template_id || entry?.template_id || entry?.id || lead.last_tender_template_id || null,
      templateTitle:
        entry?.tender_template_title || entry?.template_title || entry?.title || tenderTemplateTitle || null,
      templateBody:
        entry?.tender_template_body || entry?.template_body || entry?.body || tenderTemplateBody || null,
      note: entry?.tender_note || entry?.note || tenderNote || null,
      changedAt: entry?.changed_at || entry?.tender_changed_at || entry?.created_at || lead.last_tender_changed_at || null
    }))
    .filter((entry) => entry.templateTitle || entry.templateBody || entry.note || entry.changedAt);

  if (tenderHistoryEntries.length === 0 && (tenderTemplateTitle || tenderTemplateBody || tenderNote)) {
    tenderHistoryEntries.push({
      templateId: lead.last_tender_template_id || 'legacy',
      templateTitle: tenderTemplateTitle || null,
      templateBody: tenderTemplateBody || null,
      note: tenderNote || null,
      changedAt: lead.last_tender_changed_at || null
    });
  }

  const showTenderInfo = tenderHistoryEntries.length > 0;

  async function handleRegisterCallAttempt() {
    setCallAttemptLoading(true);
    try {
      const res = await api(`/api/leads/${lead.id}/call-attempt`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke registrere opkald');
      }
      const data = (await res.json().catch(() => ({}))) as {
        callAttemptCount?: number;
        lastCallAt?: string;
      } | null;
      const attemptCount = Math.max(1, Number(data?.callAttemptCount ?? callAttemptCount + 1));
      const lastCallDisplay = data?.lastCallAt ? formatDateTimeShort(data.lastCallAt) : null;
      const successText = lastCallDisplay
        ? `Opkald registreret (forsøg #${attemptCount}) · senest ${lastCallDisplay}`
        : `Opkald registreret (forsøg #${attemptCount})`;
      message.success(successText);
      await refresh();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke registrere opkald');
    } finally {
      setCallAttemptLoading(false);
    }
  }

  async function handleToggleHidden(nextHidden: boolean) {
    setVisibilityLoading(true);
    try {
      const res = await api(`/api/leads/${lead.id}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: nextHidden })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere synlighed');
      }
      await res.json().catch(() => null);
      message.success(nextHidden ? 'Lead skjult' : 'Lead gjort synlig');
      await refresh();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere synlighed');
    } finally {
      setVisibilityLoading(false);
    }
  }

  const deleteLead = async (force?: boolean) => {
    const query = force ? '?force=1' : '';
    const res = await api(`/api/leads/${lead.id}${query}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Kunne ikke slette lead');
    }
    message.success(force ? 'Lead slettet (force)' : 'Lead slettet');
    await refresh();
  };

  const companyLabel = typeof lead.company_name === 'string' && lead.company_name.trim()
    ? lead.company_name.trim()
    : 'dette lead';

  function showForceDeleteConfirm() {
    Modal.confirm({
      title: 'Slet lead permanent',
      content: `${companyLabel} har registrerede udbetalinger. Hvis du fortsætter, slettes udbetalingerne permanent.`,
      okText: 'Slet alligevel',
      cancelText: 'Annuller',
      okButtonProps: { danger: true },
      onOk: () => attemptDelete(true)
    });
  }

  function attemptDelete(forceDelete?: boolean) {
    deleteLead(forceDelete).catch((error) => {
      const messageText = error instanceof Error ? error.message : 'Kunne ikke slette lead';
      if (!forceDelete && messageText === 'Lead har udbetalinger og kan ikke slettes') {
        showForceDeleteConfirm();
        return;
      }
      message.error(messageText);
    });
  }

  const confirmDelete = () => {
    Modal.confirm({
      title: 'Slet lead',
      content: `Er du sikker på, at du vil slette ${companyLabel}?`,
      okText: 'Ja, slet',
      cancelText: 'Annuller',
      okButtonProps: { danger: true },
      onOk: () => attemptDelete()
    });
  };

  return (
    <>
      <Card
        className="lead-card"
        title={lead.company_name}
        extra={
        <Space size="small" align="center">
          <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => onOpenDetail?.(lead.id)}>
            Åbn {itemLabel}
          </Button>
          {canManage && (
            <>
              <Button
                size="small"
                onClick={handleRegisterCallAttempt}
                loading={callAttemptLoading}
              >
                Log opkald
              </Button>
              <Button
                size="small"
                onClick={() => handleToggleHidden(!isHidden)}
                loading={visibilityLoading}
              >
                {isHidden ? 'Gør synlig' : 'Skjul lead'}
              </Button>
              <Button danger size="small" icon={<DeleteOutlined />} onClick={confirmDelete}>
                Slet
              </Button>
            </>
          )}
          <Tag color={tagColor} icon={<TagOutlined />}>{statusLabel}</Tag>
          <Tooltip title={tierDescription || undefined}>
            <Tag color={tierColor}>{tierLabel}</Tag>
          </Tooltip>
          {isHidden && <Tag color="default">Skjult</Tag>}
              <Tooltip title={lastCallFull || undefined}>
                <Tag color={callAttemptCount > 0 ? 'processing' : 'default'}>
                  Opkald: {callAttemptCount}
                  {callAttemptCount > 0 && lastCallShort ? ` · senest ${lastCallShort}` : ''}
                </Tag>
              </Tooltip>
          <Space size={4}>
            <DollarCircleOutlined />
            <span>{formatAmount(lead.contract_value_annual, lead.currency)}</span>
          </Space>
        </Space>
      }
    >
      {hasDetails && (
        <Space direction="vertical" size={4} style={{ marginBottom: 12 }}>
          {hasCvr && (
            <div>
              <IdcardOutlined style={{ marginRight: 6 }} />
              <strong>CVR:</strong> {formattedCvr}
            </div>
          )}
          {lead.contact_name && (
            <div>
              <UserOutlined style={{ marginRight: 6 }} />
              <strong>Kontaktperson:</strong> {lead.contact_name}
            </div>
          )}
          {lead.contact_email && (
            <div>
              <MailOutlined style={{ marginRight: 6 }} />
              <strong>E-mail:</strong>{' '}
              <a href={`mailto:${lead.contact_email}`}>{lead.contact_email}</a>
            </div>
          )}
          {lead.contact_phone && (
            <div>
              <PhoneOutlined style={{ marginRight: 6 }} />
              <strong>Telefon:</strong> {lead.contact_phone}
            </div>
          )}
          {employeesDisplay !== null && (
            <div>
              <TeamOutlined style={{ marginRight: 6 }} />
              <strong>Ansatte:</strong> {employeesDisplay}
              {employeesYear ? ` (pr. ${employeesYear})` : ''}
            </div>
          )}
          {lead.notes && (
            <div>
              <FileTextOutlined style={{ marginRight: 6 }} />
              <strong>Noter:</strong> {lead.notes}
            </div>
          )}
          {hasReferrerDetails && (
            <div>
              <TeamOutlined style={{ marginRight: 6 }} />
              <strong>Referrer:</strong>{' '}
              <span>{lead.ref_full_name || 'Ukendt'}</span>
              {lead.ref_email && (
                <>
                  {' '}(<a href={`mailto:${lead.ref_email}`}>{lead.ref_email}</a>)
                </>
              )}
              {lead.ref_phone && (
                <>
                  {' '}• {lead.ref_phone}
                </>
              )}
            </div>
          )}
          {leadLabels.length > 0 && (
            <Space wrap size={4} style={{ marginTop: 4 }}>
              {leadLabels.map((label: any) => (
                <Tag key={label.id} color={label.color || 'default'}>
                  {label.name}
                </Tag>
              ))}
            </Space>
          )}
          {canManage && onAttachLabels && (
            <Space size={8} wrap style={{ marginTop: 8 }}>
              <Select
                mode="multiple"
                placeholder="Tilføj labels"
                value={selectedLabelIds}
                onChange={setSelectedLabelIds}
                options={assignableLabels.map((label) => ({ value: label.id, label: label.name }))}
                optionFilterProp="label"
                maxTagCount="responsive"
                style={{ minWidth: 220 }}
                disabled={assignableLabels.length === 0 || attachingLabels}
              />
              <Button
                size="small"
                type="primary"
                loading={attachingLabels}
                disabled={selectedLabelIds.length === 0}
                onClick={() => void attachLabels()}
              >
                Tilføj labels
              </Button>
            </Space>
          )}
        </Space>
      )}
      {showTenderInfo && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 8,
            border: '1px solid #bae7ff',
            background: '#e6f7ff'
          }}
        >
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>Sagstekst</Text>
            {tenderHistoryEntries.map((entry, index) => {
              const shortTs = formatDateTimeShort(entry.changedAt ?? null);
              const fullTs = formatDateTimeFull(entry.changedAt ?? null);
              const key = `${entry.templateId || 'entry'}-${entry.changedAt || index}`;
              return (
                <div
                  key={key}
                  style={{
                    paddingTop: index === 0 ? 0 : 8,
                    marginTop: index === 0 ? 0 : 8,
                    borderTop: index === 0 ? undefined : '1px solid #d9f0ff'
                  }}
                >
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Tooltip title={fullTs || shortTs || entry.changedAt || 'Ukendt tidspunkt'}>
                      <Text type="secondary">{shortTs || 'Ukendt tidspunkt'}</Text>
                    </Tooltip>
                    {entry.templateTitle && <Text>{entry.templateTitle}</Text>}
                    {entry.templateBody && (
                      <Text style={{ whiteSpace: 'pre-line' }}>{entry.templateBody}</Text>
                    )}
                    {entry.note && (
                      <Text type="secondary" style={{ whiteSpace: 'pre-line' }}>
                        {entry.note}
                      </Text>
                    )}
                  </Space>
                </div>
              );
            })}
          </Space>
        </div>
      )}
      <Steps current={currentIndex} items={stepItems} size="small" />
      {canManage ? (
        <PipelineActionButtons
          actions={actions}
          cancelLabel={cancelLabel}
          loading={statusUpdating}
          disabled={statusUpdating}
          onAction={(action) => {
            void handleTransition(action.to);
          }}
          getActionTooltip={(action) =>
            action.to === 'SIGNED' && !financialReady
              ? 'Opdater økonomi før lead kan markeres som underskrevet'
              : null
          }
          primaryExtras={<Button onClick={() => onEditFinancials?.(lead)}>Opdater økonomi</Button>}
          style={{ marginTop: 12 }}
        />
      ) : null}
      </Card>

    </>
  );
}

function Login({ onOk }: { onOk: (claims: Claims) => void }) {
  const [loading, setLoading] = useState(false);

  async function onFinish(values: any) {
    try {
      setLoading(true);
      clearTokens();
      const res = await api('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      if (!res.ok) {
        const errorPayload = await res.json().catch(() => ({}));
        const errorMessage =
          typeof errorPayload?.error === 'string' && errorPayload.error.trim()
            ? errorPayload.error.trim()
            : res.status === 401
              ? 'E-mail eller adgangskode er forkert'
              : `Login fejlede (${res.status})`;
        return message.error(errorMessage);
      }
      const payload: any = await res.json();
      const { access, refresh } = payload;
      const claims = decodeClaims(access);
      if (!claims) {
        return message.error('Kunne ikke læse login-oplysninger');
      }
      setTokens(access, refresh);
      try {
        window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, access);
        if (refresh) {
          window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh);
        } else {
          window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        }
      } catch (storageError) {
        console.warn('Kunne ikke gemme login-token', storageError);
      }
      onOk(claims);
    } catch (error) {
      console.error('Login request failed', error);
      message.error('Login kunne ikke gennemføres. Kontrollér at backend kører.');
    } finally {
      setLoading(false);
    }
  }
  return (
    <Form onFinish={onFinish} style={{ maxWidth: 320, margin: '80px auto' }}>
      <Form.Item name="email" rules={[{ required: true }]}>
        <Input placeholder="E-mail eller brugernavn" />
      </Form.Item>
      <Form.Item name="password" rules={[{ required: true }]}>
        <Input.Password placeholder="Adgangskode" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          Log ind
        </Button>
      </Form.Item>
    </Form>
  );
}

function AppRoutes({ user, setUser }: { user: Claims; setUser: (user: Claims | null) => void }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [referrers, setReferrers] = useState<Array<{
    id: string;
    label: string;
    email?: string;
    phone?: string;
    company?: string;
  }>>([]);
  const [labelOptions, setLabelOptions] = useState<Label[]>([]);
  const [leadForm] = Form.useForm();
  const [referrerForm] = Form.useForm();
  const [financialForm] = Form.useForm();
  const [createReferrerOpen, setCreateReferrerOpen] = useState(false);
  const [createReferrerLoading, setCreateReferrerLoading] = useState(false);
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const [financialModalLoading, setFinancialModalLoading] = useState(false);
  const [financialLead, setFinancialLead] = useState<any | null>(null);
  //const [activeView, setActiveView] = useState<'leads' | 'referrers' | 'companies'>('leads');
  const defaultActiveView: ActiveView = user.role === 'BROKER_ADMIN' ? 'dashboard' : 'leads';
  const [activeView, setActiveView] = useState<ActiveView>(defaultActiveView);
  const [pendingWorkListId, setPendingWorkListId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [leadTierFilter, setLeadTierFilter] = useState<'ALL' | LeadTier>('ALL');
  const [createCvrSummary, setCreateCvrSummary] = useState<CvrSummary | null>(null);
  const [createCvrLookupLoading, setCreateCvrLookupLoading] = useState(false);
  const [lastSuccessfulCvr, setLastSuccessfulCvr] = useState<string | null>(null);
  const createCvrLookupSeq = useRef(0);

  const tierCounts = useMemo(() => {
    const buckets: Record<LeadTier, number> = {
      LEAD: 0,
      IN_PROGRESS: 0,
      CUSTOMER: 0
    };
    for (const lead of leads) {
      const tier = deriveLeadTier(lead.status);
      buckets[tier] += 1;
    }
    return buckets;
  }, [leads]);

  const activeLeadCount = useMemo(() => {
    return leads.filter((lead) => lead.status !== 'PAID_OUT' && lead.status !== 'NO_CONTRACT').length;
  }, [leads]);

  const visibleLeads = useMemo(() => {
    if (leadTierFilter === 'ALL') {
      return leads;
    }
    return leads.filter((lead) => deriveLeadTier(lead.status) === leadTierFilter);
  }, [leads, leadTierFilter]);
  const [companyLookupOptions, setCompanyLookupOptions] = useState<Array<{
    value: string;
    label: string;
    cvrnummer?: string;
    virksomhed_id?: string;
  }>>([]);
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const companyLookupSeq = useRef(0);
  const companyLookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [referrerCvrSummary, setReferrerCvrSummary] = useState<CvrSummary | null>(null);
  const [referrerCvrLookupLoading, setReferrerCvrLookupLoading] = useState(false);
  const [referrerLastSuccessfulCvr, setReferrerLastSuccessfulCvr] = useState<string | null>(null);
  const referrerCvrLookupSeq = useRef(0);
  const [referrerCompanyOptions, setReferrerCompanyOptions] = useState<Array<{
    value: string;
    label: string;
    cvrnummer?: string;
    virksomhed_id?: string;
  }>>([]);
  const [referrerCompanyLookupLoading, setReferrerCompanyLookupLoading] = useState(false);
  const referrerCompanyLookupSeq = useRef(0);
  const referrerCompanyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionExpiredRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onDetailPage = location.pathname.startsWith('/leads/');
  const [includeHiddenLeads, setIncludeHiddenLeads] = useState(false);
  const [showMonitoredLeads, setShowMonitoredLeads] = useState(false);
  const [leadListLoading, setLeadListLoading] = useState(false);
  const [leadListHasMore, setLeadListHasMore] = useState(false);
  const [leadDetailModalOpen, setLeadDetailModalOpen] = useState(false);
  const [leadDetailModalLeadId, setLeadDetailModalLeadId] = useState<string | null>(null);
  const [leadDetailHideInsuranceTypes, setLeadDetailHideInsuranceTypes] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState<CalendarEvent[]>([]);
  const [upcomingMeetingsLoading, setUpcomingMeetingsLoading] = useState(false);
  const [upcomingTodos, setUpcomingTodos] = useState<LeadTodo[]>([]);
  const [upcomingTodosLoading, setUpcomingTodosLoading] = useState(false);
  const [dashboardRecentLeads, setDashboardRecentLeads] = useState<any[]>([]);
  const [dashboardRecentLeadsLoading, setDashboardRecentLeadsLoading] = useState(false);
  const [dashboardPropertyPortfolios, setDashboardPropertyPortfolios] = useState<any[]>([]);
  const [dashboardPropertyPortfoliosLoading, setDashboardPropertyPortfoliosLoading] = useState(false);
  const [dashboardPropertyPortfoliosError, setDashboardPropertyPortfoliosError] = useState<string | null>(null);
  const [dashboardPropertyAlerts, setDashboardPropertyAlerts] = useState<any[]>([]);
  const [dashboardPropertyAlertsLoading, setDashboardPropertyAlertsLoading] = useState(false);
  const [dashboardIncludeHiddenRecentLeads, setDashboardIncludeHiddenRecentLeads] = useState(() => {
    return localStorage.getItem(DASHBOARD_INCLUDE_HIDDEN_RECENT_LEADS_STORAGE_KEY) === '1';
  });
  const [pipelineHistoryWindow, setPipelineHistoryWindow] = useState(30);
  const [pipelineHistoryData, setPipelineHistoryData] = useState<PipelineHistoryResponse | null>(null);
  const [pipelineHistoryLoading, setPipelineHistoryLoading] = useState(false);
  const [pipelineHistoryError, setPipelineHistoryError] = useState<string | null>(null);
  const pipelineHistoryRequestSeq = useRef(0);
  const watchedCreateCvr = Form.useWatch('cvrnummer', leadForm);
  const watchedReferrerCvr = Form.useWatch('cvrnummer', referrerForm);
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [agreementModalLoading, setAgreementModalLoading] = useState(false);
  const [agreementSubmitting, setAgreementSubmitting] = useState(false);
  const [agreementSavingDraft, setAgreementSavingDraft] = useState(false);
  const [agreementInitialValues, setAgreementInitialValues] = useState<any>({});
  const [agreementCustomerId, setAgreementCustomerId] = useState<string | null>(null);
  const [agreementLeadId, setAgreementLeadId] = useState<string | null>(null);
  const [agreementAvailableInsuranceTypes, setAgreementAvailableInsuranceTypes] = useState<InsuranceType[]>([]);
  const [agreementSelectedInsuranceTypeIds, setAgreementSelectedInsuranceTypeIds] = useState<string[]>([]);

  const isAdmin = user?.role === 'BROKER_ADMIN';
  const isBroker = user?.role === 'BROKER';

  useEffect(() => {
    if (onDetailPage) {
      setLeadDetailModalOpen(false);
      setLeadDetailModalLeadId(null);
    }
  }, [onDetailPage]);

  const loadLabelOptions = useCallback(async () => {
    if (!isAdmin) {
      setLabelOptions([]);
      return;
    }
    const res = await api('/api/labels');
    if (res.ok) {
      const data = (await res.json()) as Label[];
      setLabelOptions(
        data.slice().sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
      );
    } else if (res.status !== 403) {
      message.error('Kunne ikke hente labels');
    }
  }, [isAdmin]);

  const attachLabelsToLead = useCallback(async (leadId: string, labelIds: string[]) => {
    const response = await api(`/api/leads/${leadId}/labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ labelIds })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((data as any)?.error || 'Kunne ikke tilknytte labels');
    }
    const attached: Label[] = Array.isArray((data as any)?.labels)
      ? (data as any).labels
      : data?.id ? [data as Label] : [];
    setLeads((current) => current.map((lead) => {
      if (lead.id !== leadId) {
        return lead;
      }
      const nextById = new Map((Array.isArray(lead.labels) ? lead.labels : []).map((label: Label) => [label.id, label]));
      attached.forEach((label) => nextById.set(label.id, label));
      return {
        ...lead,
        labels: [...nextById.values()].sort((left, right) => left.name.localeCompare(right.name, 'da', { sensitivity: 'base' }))
      };
    }));
    message.success(attached.length === 1 ? 'Label tilknyttet' : `${attached.length} labels tilknyttet`);
  }, []);

  const loadUpcomingMeetings = useCallback(async () => {
    const now = dayjs();
    const rangeStart = now.startOf('day');
    const rangeEnd = now.add(UPCOMING_MEETING_WINDOW_DAYS, 'day').endOf('day');
    setUpcomingMeetingsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', rangeStart.toISOString());
      params.set('to', rangeEnd.toISOString());
      const res = await api(`/api/calendar?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente møder');
      }
      const payload = (await res.json()) as RawCalendarEvent[];
      const normalized = payload
        .map(mapRawCalendarEvent)
        .filter((event) => Boolean(event.startAt))
        .filter((event) => {
          if (!event.startAt) {
            return false;
          }
          const startTs = dayjs(event.startAt);
          if (!startTs.isValid()) {
            return false;
          }
          return !startTs.isBefore(rangeStart) && !startTs.isAfter(rangeEnd);
        })
        .sort((a, b) => {
          const aTime = a.startAt ? dayjs(a.startAt).valueOf() : 0;
          const bTime = b.startAt ? dayjs(b.startAt).valueOf() : 0;
          return aTime - bTime;
        })
        .slice(0, DASHBOARD_SECTION_LIMIT);
      setUpcomingMeetings(normalized);
    } catch (error: any) {
      console.error('Failed to load upcoming meetings', error);
      message.error(error?.message || 'Kunne ikke hente møder');
    } finally {
      setUpcomingMeetingsLoading(false);
    }
  }, []);

  const loadUpcomingTodos = useCallback(async () => {
    if (!isAdmin) {
      setUpcomingTodos([]);
      return;
    }
    setUpcomingTodosLoading(true);
    try {
      const openStatuses: LeadTodoStatus[] = ['NEW', 'IN_PROGRESS', 'HANDLED'];
      const todos = await fetchAllLeadTodos({ status: openStatuses });
      const now = dayjs();
      const windowEnd = now.add(UPCOMING_TODO_WINDOW_DAYS, 'day').endOf('day');
      const upcoming = todos
        .filter((todo) => Boolean(todo.due_at))
        .filter((todo) => {
          if (!todo.due_at) {
            return false;
          }
          const due = dayjs(todo.due_at);
          if (!due.isValid()) {
            return false;
          }
          // Include both overdue (past) and upcoming (within window)
          return !due.isAfter(windowEnd);
        })
        .sort((a, b) => {
          const dueA = a.due_at ? dayjs(a.due_at).valueOf() : Number.POSITIVE_INFINITY;
          const dueB = b.due_at ? dayjs(b.due_at).valueOf() : Number.POSITIVE_INFINITY;
          return dueA - dueB;
        })
        .slice(0, DASHBOARD_SECTION_LIMIT);
      setUpcomingTodos(upcoming);
    } catch (error: any) {
      console.error('Failed to load upcoming todos', error);
      message.error(error?.message || 'Kunne ikke hente opgaver');
    } finally {
      setUpcomingTodosLoading(false);
    }
  }, [isAdmin]);

  const loadDashboardRecentLeads = useCallback(async () => {
    setDashboardRecentLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '10');
      params.set('sort', 'recent');
      if (dashboardIncludeHiddenRecentLeads) {
        params.set('includeHidden', '1');
      }
      const res = await api(`/api/leads?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente senest opdaterede leads');
      }
      const payload = await res.json();
      const data = (Array.isArray(payload) ? payload : payload?.items || []) as any[];
      const recent = data
        .slice()
        .sort((a, b) => {
          const aTime = Math.max(
            dayjs(a.updated_at || a.created_at).valueOf(),
            dayjs(a.last_policy_activity_at || a.created_at).valueOf()
          );
          const bTime = Math.max(
            dayjs(b.updated_at || b.created_at).valueOf(),
            dayjs(b.last_policy_activity_at || b.created_at).valueOf()
          );
          return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
        })
        .slice(0, 10);
      setDashboardRecentLeads(recent);
    } catch (error: any) {
      console.error('Failed to load dashboard recent leads', error);
      message.error(error?.message || 'Kunne ikke hente senest opdaterede leads');
    } finally {
      setDashboardRecentLeadsLoading(false);
    }
  }, [dashboardIncludeHiddenRecentLeads]);

  const loadDashboardPropertyPortfolios = useCallback(async () => {
    if (!isAdmin) {
      setDashboardPropertyPortfolios([]);
      return;
    }
    setDashboardPropertyPortfoliosLoading(true);
    setDashboardPropertyPortfoliosError(null);
    try {
      const res = await api('/api/leads/property-portfolios');
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as any)?.error || 'Kunne ikke hente ejendomsporteføljer');
      }
      setDashboardPropertyPortfolios(Array.isArray((payload as any)?.portfolios) ? (payload as any).portfolios : []);
    } catch (error: any) {
      console.error('Failed to load dashboard property portfolios', error);
      setDashboardPropertyPortfolios([]);
      setDashboardPropertyPortfoliosError(error?.message || 'Kunne ikke hente ejendomsporteføljer');
    } finally {
      setDashboardPropertyPortfoliosLoading(false);
    }
  }, [isAdmin]);

  const loadDashboardPropertyAlerts = useCallback(async () => {
    if (!isAdmin) {
      setDashboardPropertyAlerts([]);
      return;
    }
    setDashboardPropertyAlertsLoading(true);
    try {
      const res = await api('/api/leads/property-alerts');
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as any)?.error || 'Kunne ikke hente ejendomsalarmer');
      }
      setDashboardPropertyAlerts(Array.isArray((payload as any)?.alerts) ? (payload as any).alerts : []);
    } catch (error: any) {
      console.error('Failed to load dashboard property alerts', error);
      setDashboardPropertyAlerts([]);
      message.error(error?.message || 'Kunne ikke hente ejendomsalarmer');
    } finally {
      setDashboardPropertyAlertsLoading(false);
    }
  }, [isAdmin]);

  const handleDashboardIncludeHiddenRecentLeadsChange = useCallback((checked: boolean) => {
    setDashboardIncludeHiddenRecentLeads(checked);
    if (checked) {
      localStorage.setItem(DASHBOARD_INCLUDE_HIDDEN_RECENT_LEADS_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(DASHBOARD_INCLUDE_HIDDEN_RECENT_LEADS_STORAGE_KEY);
    }
  }, []);

  const loadPipelineHistory = useCallback(
    async (windowDays: number) => {
      if (!isAdmin) {
        return;
      }
      const requestId = ++pipelineHistoryRequestSeq.current;
      setPipelineHistoryLoading(true);
      setPipelineHistoryError(null);
      try {
        const response = await fetchPipelineHistory(windowDays);
        if (requestId !== pipelineHistoryRequestSeq.current) {
          return;
        }
        setPipelineHistoryData(response);
      } catch (error: any) {
        if (requestId !== pipelineHistoryRequestSeq.current) {
          return;
        }
        setPipelineHistoryData(null);
        setPipelineHistoryError(error?.message || 'Kunne ikke hente pipelinehistorik');
      } finally {
        if (requestId === pipelineHistoryRequestSeq.current) {
          setPipelineHistoryLoading(false);
        }
      }
    },
    [isAdmin]
  );

  const handlePipelineWindowChange = useCallback((nextWindow: number) => {
    const allowed = nextWindow === 7 || nextWindow === 14 || nextWindow === 30;
    if (!allowed) {
      return;
    }
    setPipelineHistoryWindow((current) => (current === nextWindow ? current : nextWindow));
  }, []);

  const buildAddressFromSummary = useCallback((summary: CvrSummary | null) => {
    if (!summary) {
      return '';
    }

    const segments: string[] = [];
    const streetParts: string[] = [];

    if (summary.vejnavn) {
      streetParts.push(summary.vejnavn.trim());
    }

    const from = summary.husnummer_fra;
    const to = summary.husnummer_til;
    let houseNumber = '';
    if (from && to && to !== from) {
      houseNumber = `${from}-${to}`;
    } else if (from) {
      houseNumber = from;
    } else if (to) {
      houseNumber = to;
    }

    if (houseNumber) {
      streetParts.push(houseNumber);
    }

    if (summary.etage) {
      streetParts.push(`${summary.etage}.`);
    }

    if (summary.doer) {
      streetParts.push(summary.doer);
    }

    if (streetParts.length) {
      segments.push(streetParts.join(' ').replace(/\s+/g, ' ').trim());
    }

    if (summary.supplerende_bynavn) {
      segments.push(summary.supplerende_bynavn);
    }

    const postalParts: string[] = [];
    if (summary.postnummer) {
      postalParts.push(summary.postnummer);
    }
    if (summary.postdistrikt) {
      postalParts.push(summary.postdistrikt);
    }
    if (postalParts.length) {
      segments.push(postalParts.join(' '));
    }

    if (summary.kommunenavn) {
      segments.push(summary.kommunenavn);
    }

    return segments.join(', ');
  }, []);

  const mapCompanyResultsToOptions = useCallback(
    (data: CompanyLookupResult[]) =>
      data
        .filter((item) => (item.navn && item.navn.trim()) || item.cvrnummer)
        .map((item) => {
          const name = (item.navn || '').trim();
          const cvr = item.cvrnummer ? item.cvrnummer.replace(/\D+/g, '') : '';
          const displayCvr = cvr ? formatCvrNumber(cvr) : '';
          const baseLabel = name || (displayCvr || 'Ukendt virksomhed');
          const label = displayCvr ? `${baseLabel} (${displayCvr})` : baseLabel;
          const valueToUse = name || displayCvr || baseLabel;
          return {
            value: valueToUse,
            label,
            cvrnummer: cvr || undefined,
            virksomhed_id: item.virksomhed_id
          };
        }),
    []
  );

  const loadLeads = useCallback(
    async (options?: {
      search?: string;
      labels?: string[];
      includeHidden?: boolean;
      monitored?: boolean;
      offset?: number;
      append?: boolean;
    }) => {
      const effectiveSearch =
        options && Object.prototype.hasOwnProperty.call(options, 'search')
          ? (options.search ?? '')
          : searchTerm;
      const effectiveLabels =
        options && Object.prototype.hasOwnProperty.call(options, 'labels')
          ? options.labels ?? []
          : selectedLabelIds;
      const effectiveIncludeHidden =
        options && Object.prototype.hasOwnProperty.call(options, 'includeHidden')
          ? Boolean(options.includeHidden)
          : includeHiddenLeads;
      const effectiveMonitored = Boolean(options?.monitored ?? showMonitoredLeads);
      const includeHiddenForRequest = effectiveIncludeHidden || effectiveMonitored;
      const effectiveOffset = Math.max(0, options?.offset ?? 0);
      const append = Boolean(options?.append && effectiveOffset > 0);

      const params = new URLSearchParams();
      if (effectiveSearch) {
        params.set('q', effectiveSearch);
      }
      if (effectiveLabels.length > 0) {
        for (const id of effectiveLabels) {
          if (id) {
            params.append('label', id);
          }
        }
      }
      params.set('includeLabels', '1');
      params.set('limit', '50');
      params.set('offset', String(effectiveOffset));
      if (effectiveMonitored) {
        params.set('actionKey', 'MONITORED');
      }
      if (includeHiddenForRequest) {
        params.set('includeHidden', '1');
      }

      const query = params.toString() ? `?${params.toString()}` : '';
      setLeadListLoading(true);
      try {
        const res = await api(`/api/leads${query}`);
        if (!res.ok) {
          if (res.status !== 401) {
            message.error('Kunne ikke hente leads');
          }
          return;
        }
        const payload = await res.json();
        const data = Array.isArray(payload) ? payload : payload?.items;
        const items = Array.isArray(data) ? data : [];
        setLeadListHasMore(Boolean(!Array.isArray(payload) ? payload?.hasMore : false));
        setLeads((current) => (append ? [...current, ...items] : items));
      } finally {
        setLeadListLoading(false);
      }
    },
    [includeHiddenLeads, searchTerm, selectedLabelIds, showMonitoredLeads]
  );

  const fetchLeadById = useCallback(async (id: string) => {
    const res = await api(`/api/leads/${id}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((data as any)?.error || 'Kunne ikke hente lead');
    }
    return data;
  }, []);

  const refreshSingleLead = useCallback(
    async (leadId: string) => {
      const updated = await fetchLeadById(leadId);
      setLeads((current) => {
        let found = false;
        const next = current.map((lead) => {
          if (lead.id === leadId) {
            found = true;
            return { ...lead, ...updated };
          }
          return lead;
        });
        return found ? next : [...next, updated];
      });
      return updated;
    },
    [fetchLeadById]
  );

  const refreshLeadOrFallback = useCallback(
    async (leadId?: string | null) => {
      if (!leadId) {
        return;
      }
      try {
        await refreshSingleLead(leadId);
      } catch (error) {
        console.warn('Single lead refresh failed, reloading full list', error);
        await loadLeads();
      }
    },
    [loadLeads, refreshSingleLead]
  );

  const loadAgreementInsuranceTypes = useCallback(async () => {
    try {
      const res = await api('/api/insurance-types');
      const data = (await res.json().catch(() => [])) as InsuranceType[];
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper');
      }
      const normalized = Array.isArray(data)
        ? [...data].sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
        : [];
      setAgreementAvailableInsuranceTypes(normalized);
    } catch (error) {
      console.error('Failed to load agreement insurance types', error);
    }
  }, []);

  const normalizeAgreementInsuranceTypes = (items: any[]): CustomerInsuranceType[] => {
    return (Array.isArray(items) ? items : [])
      .map((item: any) => ({
        ...item,
        insuranceTypeId: String(
          item.insuranceTypeId ??
            item.insuranceTypeID ??
            item.insurancetypeid ??
            item.insurance_type_id ??
            item.forsikringstype_id ??
            item.forsikringstypeid ??
            item.id ??
            ''
        ),
        title:
          item.title ??
          item.navn ??
          item.name ??
          item.Title ??
          item.insurance_type_title ??
          item.forsikringstype_title ??
          item.forsikringstype_navn ??
          item.forsikringstypenavn ??
          item.insuranceTypeName ??
          item.insurance_type_name ??
          ''
      }))
      .map((item) => ({
        ...item,
        title: item.title || item.insuranceTypeId
      }))
      .filter((item) => item.insuranceTypeId);
  };

  const openAgreementWizard = useCallback(
    async (leadId: string) => {
      setAgreementModalLoading(true);
      try {
        let lead = leads.find((item) => item.id === leadId) || null;
        if (!lead) {
          lead = await fetchLeadById(leadId);
        }
        if (!lead) {
          throw new Error('Lead ikke fundet');
        }
        const normalizedLeadCvr = typeof lead.cvrnummer === 'string' ? lead.cvrnummer.replace(/\D+/g, '') : '';
        if (!normalizedLeadCvr) {
          throw new Error('Tilføj CVR på leadet før agreement');
        }
        const leadCompanyName = (lead.company_name || '').trim();
        if (!leadCompanyName) {
          throw new Error('Tilføj virksomhedsnavn på leadet før agreement');
        }

        let customer: any = null;
        try {
          const customerRes = await api(`/api/customers/by-cvr/${normalizedLeadCvr}`);
          if (customerRes.ok) {
            customer = await customerRes.json();
          }
        } catch (error) {
          console.warn('Customer lookup failed', error);
        }

        if (agreementAvailableInsuranceTypes.length === 0) {
          await loadAgreementInsuranceTypes();
        }

        let cvrSummary: CvrSummary | null = null;
        const missingIndustry = !customer?.industry && !lead.industry && !lead.branchetekst;
        const missingAddress =
          !customer?.address && !customer?.postal_code && !customer?.city && !customer?.country;
        if (missingIndustry || missingAddress) {
          try {
            const summaryRes = await api(`/api/leads/lookup/cvr/${normalizedLeadCvr}`);
            if (summaryRes.ok) {
              cvrSummary = (await summaryRes.json()) as CvrSummary;
            }
          } catch (error) {
            console.warn('CVR summary lookup failed', error);
          }
        }

        const summaryIndustry = cvrSummary
          ? [cvrSummary.branchekode, cvrSummary.branchetekst].filter(Boolean).join(' - ')
          : '';
        const resolvedIndustry = customer?.industry || lead.industry || lead.branchetekst || summaryIndustry || '';

        const summaryAddress = cvrSummary ? buildAddressFromSummary(cvrSummary) : '';
        const resolvedAddress = customer?.address || lead.address || summaryAddress || '';
        const resolvedPostal = customer?.postal_code || lead.postal_code || cvrSummary?.postnummer || '';
        const resolvedCity = customer?.city || lead.city || cvrSummary?.postdistrikt || '';
        const resolvedCountry = customer?.country || lead.country || cvrSummary?.kommunenavn || '';
        const cvrLinkPayload = {
          cvrnummer: normalizedLeadCvr,
          annual_value: customer?.annual_value ?? lead.contract_value_annual ?? null,
          commission_percent: customer?.commission_percent ?? lead.commission_percent ?? null,
          commission_amount: customer?.commission_amount ?? null,
          currency: customer?.currency || lead.currency || 'DKK',
          contract_signed_at: customer?.contract_signed_at || '',
          contract_starts_at: customer?.contract_starts_at || '',
          contract_expires_at: customer?.contract_expires_at || '',
          renewal_notice_at: customer?.renewal_notice_at || ''
        };

        if (!customer) {
          try {
            const draft = await createCustomerDraftFromLead(lead.id, {
              company_name: leadCompanyName,
              cvrnummer: normalizedLeadCvr,
              primary_contact_name: lead.contact_name || '',
              primary_contact_email: lead.contact_email || '',
              primary_contact_phone: lead.contact_phone || '',
              address: resolvedAddress || '',
              postal_code: resolvedPostal || '',
              city: resolvedCity || '',
              country: resolvedCountry || '',
              industry: resolvedIndustry || '',
              annual_value: lead.contract_value_annual ?? null,
              commission_percent: lead.commission_percent ?? null,
              commission_amount: lead.commission_amount ?? null,
              currency: lead.currency || 'DKK',
              contract_signed_at: lead.contract_signed_at || '',
              contract_starts_at: lead.contract_starts_at || '',
              contract_expires_at: lead.contract_expires_at || '',
              renewal_notice_at: lead.renewal_notice_at || '',
              notes: lead.notes || '',
              cvr_links: [cvrLinkPayload]
            });
            customer = draft;
          } catch (error) {
            console.warn('Customer draft creation failed', error);
          }
        }

        const initial = {
          company_name: customer?.company_name || lead.company_name || '',
          cvrnummer: lead.cvrnummer,
          primary_contact_name: customer?.primary_contact_name || lead.contact_name || '',
          primary_contact_email: customer?.primary_contact_email || lead.contact_email || '',
          primary_contact_phone: customer?.primary_contact_phone || lead.contact_phone || '',
          address: resolvedAddress,
          postal_code: resolvedPostal,
          city: resolvedCity,
          country: resolvedCountry,
          industry: resolvedIndustry,
          annual_value: customer?.annual_value ?? lead.contract_value_annual ?? null,
          commission_percent: customer?.commission_percent ?? lead.commission_percent ?? null,
          commission_amount: customer?.commission_amount ?? null,
          currency: customer?.currency || lead.currency || 'DKK',
          contract_signed_at: customer?.contract_signed_at || '',
          contract_starts_at: customer?.contract_starts_at || '',
          contract_expires_at: customer?.contract_expires_at || '',
          renewal_notice_at: customer?.renewal_notice_at || '',
          notes: customer?.notes || ''
        };

        const insuranceTypes = normalizeAgreementInsuranceTypes(
          Array.isArray(customer?.insurance_types)
            ? customer.insurance_types
            : Array.isArray((customer as any)?.insuranceTypes)
              ? (customer as any).insuranceTypes
              : Array.isArray((lead as any)?.insurance_types)
                ? (lead as any).insurance_types
                : Array.isArray((lead as any)?.insuranceTypes)
                  ? (lead as any).insuranceTypes
                  : []
        );

        setAgreementCustomerId(customer?.id ?? null);
        setAgreementInitialValues(initial);
        setAgreementSelectedInsuranceTypeIds(insuranceTypes.map((item) => String(item.insuranceTypeId)));
        setAgreementLeadId(lead.id);
        setAgreementModalOpen(true);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke åbne agreement-vinduet');
      } finally {
        setAgreementModalLoading(false);
      }
    },
    [agreementAvailableInsuranceTypes.length, buildAddressFromSummary, fetchLeadById, leads, loadAgreementInsuranceTypes]
  );

  const handleAgreementSubmit = useCallback(
    async (values: any) => {
      if (!agreementLeadId) {
        return;
      }
      setAgreementSubmitting(true);
      const normalizedCvr = typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      const cvrLinkPayload = {
        cvrnummer: normalizedCvr,
        annual_value: values.annual_value,
        commission_percent: values.commission_percent,
        commission_amount: values.commission_amount,
        currency: values.currency,
        contract_signed_at: values.contract_signed_at,
        contract_starts_at: values.contract_starts_at,
        contract_expires_at: values.contract_expires_at,
        renewal_notice_at: values.renewal_notice_at
      };

      const persistInsuranceTypes = async (customerId: string) => {
        const res = await api(`/api/customers/${customerId}/insurance-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ insuranceTypeIds: agreementSelectedInsuranceTypeIds })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error((data as any)?.error || 'Kunne ikke opdatere forsikringstyper');
        }
      };

      const persistLeadValue = async () => {
        if (!agreementLeadId) return;
        await api(`/api/leads/${agreementLeadId}/value`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contract_value_annual: values.annual_value,
            commission_percent: values.commission_percent
          })
        });
      };

      const persistLeadContact = async () => {
        if (!agreementLeadId) return;
        await api(`/api/leads/${agreementLeadId}/detail`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_name: values.primary_contact_name,
            contact_email: values.primary_contact_email,
            contact_phone: values.primary_contact_phone,
            cvrnummer: normalizedCvr
          })
        });
      };

      try {
        let targetCustomerId = agreementCustomerId;
        if (agreementCustomerId) {
          const res = await api(`/api/customers/${agreementCustomerId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...values,
              status: 'ACTIVE',
              cvrnummer: normalizedCvr,
              cvr_links: [cvrLinkPayload]
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((data as any)?.error || 'Kunne ikke opdatere kunde');
          }
          targetCustomerId = (data as any)?.id || agreementCustomerId;
        } else {
          const res = await api('/api/customers', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...values,
              status: 'ACTIVE',
              cvrnummer: normalizedCvr,
              lead_id: agreementLeadId,
              cvr_links: [cvrLinkPayload]
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((data as any)?.error || 'Kunne ikke oprette kunde');
          }
          targetCustomerId = (data as any)?.id || null;
        }

        if (targetCustomerId) {
          await persistInsuranceTypes(targetCustomerId);
        }

        await persistLeadValue();
        await persistLeadContact();

        const statusRes = await api(`/api/leads/${agreementLeadId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toStatus: 'AGREEMENT_MADE' })
        });
        const statusData = await statusRes.json().catch(() => ({}));
        if (!statusRes.ok) {
          throw new Error((statusData as any)?.error || 'Kunne ikke opdatere status');
        }

        message.success('Kunde gemt og status sat til Agreement made');
        setAgreementModalOpen(false);
        setAgreementCustomerId(null);
        setAgreementLeadId(null);
        setAgreementSelectedInsuranceTypeIds([]);
        await loadLeads();
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke gemme agreement');
      } finally {
        setAgreementSubmitting(false);
      }
    },
    [agreementCustomerId, agreementLeadId, agreementSelectedInsuranceTypeIds, loadLeads]
  );

  const handleAgreementSaveDraft = useCallback(
    async (values: any) => {
      if (!agreementLeadId) {
        return;
      }
      setAgreementSavingDraft(true);
      const normalizedCvr = typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      if (!normalizedCvr) {
        message.error('Tilføj CVR før du gemmer kladden');
        setAgreementSavingDraft(false);
        return;
      }
      const companyName = (values.company_name || '').trim();
      if (!companyName) {
        message.error('Tilføj virksomhedsnavn før du gemmer kladden');
        setAgreementSavingDraft(false);
        return;
      }
      const cvrLinkPayload = {
        cvrnummer: normalizedCvr,
        annual_value: values.annual_value ?? null,
        commission_percent: values.commission_percent ?? null,
        commission_amount: values.commission_amount ?? null,
        currency: values.currency || 'DKK',
        contract_signed_at: values.contract_signed_at || '',
        contract_starts_at: values.contract_starts_at || '',
        contract_expires_at: values.contract_expires_at || '',
        renewal_notice_at: values.renewal_notice_at || ''
      };

      try {
        let targetCustomerId = agreementCustomerId;
        if (agreementCustomerId) {
          const res = await api(`/api/customers/${agreementCustomerId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...values,
              cvrnummer: normalizedCvr,
              status: 'ONBOARDING',
              cvr_links: [cvrLinkPayload]
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error((data as any)?.error || 'Kunne ikke gemme kladde');
          }
          targetCustomerId = (data as any)?.id || agreementCustomerId;
        } else {
          const draft = await createCustomerDraftFromLead(agreementLeadId, {
            ...values,
            cvrnummer: normalizedCvr,
            cvr_links: [cvrLinkPayload]
          });
          setAgreementCustomerId(draft?.id ?? null);
          targetCustomerId = draft?.id ?? null;
        }

        if (targetCustomerId) {
          await api(`/api/customers/${targetCustomerId}/insurance-types`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ insuranceTypeIds: agreementSelectedInsuranceTypeIds })
          });
        }

        message.success('Kladde gemt');
        setAgreementModalOpen(false);
        setAgreementSelectedInsuranceTypeIds([]);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke gemme kladde');
      } finally {
        setAgreementSavingDraft(false);
      }
    },
    [agreementCustomerId, agreementLeadId, agreementSelectedInsuranceTypeIds]
  );

  const handleSearch = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setSearchInput(value);
      setSearchTerm(trimmed);
      loadLeads({ search: trimmed });
    },
    [loadLeads]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInput(value);
      if (value === '') {
        setSearchTerm('');
        loadLeads({ search: '' });
      }
    },
    [loadLeads]
  );

  const handleLabelFilterChange = useCallback(
    (values: string[]) => {
      setSelectedLabelIds(values);
      loadLeads({ labels: values });
    },
    [loadLeads]
  );

  const handleTierFilterChange = useCallback((value: 'ALL' | LeadTier) => {
    setLeadTierFilter(value);
  }, []);

  const handleHiddenToggle = useCallback(
    (event: CheckboxChangeEvent) => {
      const checked = event.target.checked;
      setIncludeHiddenLeads(checked);
      loadLeads({ includeHidden: checked });
    },
    [loadLeads]
  );

  const handleMonitoredToggle = useCallback((event: CheckboxChangeEvent) => {
    const checked = event.target.checked;
    setShowMonitoredLeads(checked);
    loadLeads({ monitored: checked, includeHidden: checked || includeHiddenLeads });
  }, [includeHiddenLeads, loadLeads]);

  const loadReferrers = useCallback(async () => {
    if (!isAdmin) return;
    const res = await api('/api/referrers');
    if (res.ok) {
      setReferrers(await res.json());
    } else if (res.status !== 403) {
      message.error('Kunne ikke hente referrers');
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!user) return;
    loadLeads();
  }, [user, loadLeads]);

  useEffect(() => {
    if (activeView !== 'dashboard') {
      return;
    }
    loadDashboardRecentLeads();
    loadUpcomingMeetings();
    loadUpcomingTodos();
    loadDashboardPropertyPortfolios();
    loadDashboardPropertyAlerts();
  }, [activeView, loadDashboardRecentLeads, loadUpcomingMeetings, loadUpcomingTodos, loadDashboardPropertyPortfolios, loadDashboardPropertyAlerts]);

  useEffect(() => {
    if (!isAdmin) {
      pipelineHistoryRequestSeq.current += 1;
      setPipelineHistoryData(null);
      setPipelineHistoryError(null);
      setPipelineHistoryLoading(false);
      return;
    }
    if (activeView !== 'dashboard') {
      return;
    }
    loadPipelineHistory(pipelineHistoryWindow);
  }, [isAdmin, activeView, pipelineHistoryWindow, loadPipelineHistory]);

  useEffect(() => {
    if (!user || user.role !== 'BROKER_ADMIN') return;
    loadReferrers();
    loadLabelOptions();
  }, [user, loadReferrers, loadLabelOptions]);

  useEffect(() => {
    if (!isAdmin && !(isBroker && activeView === 'workLists') && activeView !== 'leads') {
      setActiveView('leads');
    }
  }, [isAdmin, isBroker, activeView, setActiveView]);

  useEffect(() => {
    if (!isAdmin) {
      setLabelOptions([]);
      setSelectedLabelIds([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (selectedLabelIds.length === 0) {
      return;
    }
    const validIds = selectedLabelIds.filter((id) =>
      labelOptions.some((label) => label.id === id)
    );
    if (validIds.length !== selectedLabelIds.length) {
      setSelectedLabelIds(validIds);
      loadLeads({ labels: validIds });
    }
  }, [labelOptions, selectedLabelIds, loadLeads]);

  useEffect(() => {
    if (onDetailPage) {
      setActiveView('leads');
    }
  }, [onDetailPage, setActiveView]);

  useEffect(() => {
    if (onDetailPage) {
      return;
    }
    if (location.pathname !== '/' || !location.search) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const requestedView = params.get('view');
    if ((requestedView === 'arbejdslister' || requestedView === 'companies') && isAdmin) {
      const requestedWorkListId = params.get('workListId');
      setPendingWorkListId(requestedWorkListId || null);
      if (activeView !== 'companies') {
        setActiveView('companies');
      }
      return;
    }

    if (activeView !== 'companies') {
      setActiveView('companies');
    }
  }, [location.pathname, location.search, onDetailPage, activeView, setActiveView, isAdmin]);

  useEffect(() => {
    if (!createOpen) {
      setCreateCvrSummary(null);
      setCreateCvrLookupLoading(false);
      setLastSuccessfulCvr(null);
      createCvrLookupSeq.current += 1;
      setCompanyLookupOptions([]);
      setCompanyLookupLoading(false);
      if (companyLookupTimer.current) {
        clearTimeout(companyLookupTimer.current);
        companyLookupTimer.current = null;
      }
      companyLookupSeq.current += 1;
      return;
    }

    const raw = typeof watchedCreateCvr === 'string' ? watchedCreateCvr : '';
    const digits = raw.replace(/\D+/g, '');

    if (!digits) {
      setCreateCvrLookupLoading(false);
      setCreateCvrSummary(null);
      setLastSuccessfulCvr(null);
      leadForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    if (digits.length !== 8) {
      const errMsg = 'CVR-nummer skal bestå af 8 cifre';
      setCreateCvrLookupLoading(false);
      setCreateCvrSummary(null);
      setLastSuccessfulCvr(null);
      leadForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
      return;
    }

    if (digits === lastSuccessfulCvr) {
      return;
    }

    leadForm.setFields([{ name: 'cvrnummer', errors: [] }]);
    const lookupId = ++createCvrLookupSeq.current;
    setCreateCvrLookupLoading(true);

    (async () => {
      try {
        const res = await api(`/api/leads/lookup/cvr/${digits}`);
        if (lookupId !== createCvrLookupSeq.current) {
          return;
        }

        if (!res.ok) {
          let errMsg = 'Kunne ikke slå CVR op';
          if (res.status === 404) {
            errMsg = 'Ingen virksomhed fundet for CVR-nummeret';
          } else if (res.status === 400) {
            errMsg = 'CVR-nummer skal bestå af 8 cifre';
          }
          setCreateCvrSummary(null);
          setLastSuccessfulCvr(null);
          leadForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
          message.error(errMsg);
          return;
        }

        const summary = (await res.json()) as CvrSummary;
        if (lookupId !== createCvrLookupSeq.current) {
          return;
        }

        setCreateCvrSummary(summary);
        setLastSuccessfulCvr(digits);
        const updates: Record<string, unknown> = {};
        const existingName = leadForm.getFieldValue('company_name');
        const summaryName = summary.navn ? summary.navn.trim() : '';
        if ((!existingName || !String(existingName).trim()) && summaryName) {
          updates.company_name = summaryName;
        }
        const summaryPhone = summary.telefon ? summary.telefon.trim() : '';
        if (summaryPhone) {
          updates.contact_phone = summaryPhone;
        }
        const summaryEmail = summary.email ? summary.email.trim() : '';
        if (summaryEmail) {
          updates.contact_email = summaryEmail;
        }
        if (Object.keys(updates).length > 0) {
          leadForm.setFieldsValue(updates);
        }
        message.success(`CVR ${summary.cvrnummer || digits} fundet`);
      } catch (error) {
        if (lookupId !== createCvrLookupSeq.current) {
          return;
        }
        console.error('CVR lookup failed', error);
        const errMsg = 'Kunne ikke slå CVR op';
        setCreateCvrSummary(null);
        setLastSuccessfulCvr(null);
        message.error(errMsg);
      } finally {
        if (lookupId === createCvrLookupSeq.current) {
          setCreateCvrLookupLoading(false);
        }
      }
    })();
  }, [createOpen, watchedCreateCvr, leadForm, lastSuccessfulCvr]);

  const resetReferrerModalState = useCallback(() => {
    setReferrerCvrSummary(null);
    setReferrerCvrLookupLoading(false);
    setReferrerLastSuccessfulCvr(null);
    referrerCvrLookupSeq.current += 1;
    setReferrerCompanyOptions([]);
    setReferrerCompanyLookupLoading(false);
    if (referrerCompanyTimer.current) {
      clearTimeout(referrerCompanyTimer.current);
      referrerCompanyTimer.current = null;
    }
    referrerCompanyLookupSeq.current += 1;
  }, []);

  useEffect(() => {
    if (!createReferrerOpen) {
      resetReferrerModalState();
      return;
    }

    const raw = typeof watchedReferrerCvr === 'string' ? watchedReferrerCvr : '';
    const digits = raw.replace(/\D+/g, '');

    if (!digits) {
      setReferrerCvrLookupLoading(false);
      setReferrerCvrSummary(null);
      setReferrerLastSuccessfulCvr(null);
      referrerForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    if (digits.length !== 8) {
      const errMsg = 'CVR-nummer skal bestå af 8 cifre';
      setReferrerCvrLookupLoading(false);
      setReferrerCvrSummary(null);
      setReferrerLastSuccessfulCvr(null);
      referrerForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
      return;
    }

    if (digits === referrerLastSuccessfulCvr && referrerCvrSummary) {
      referrerForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    referrerForm.setFields([{ name: 'cvrnummer', errors: [] }]);
    const lookupId = ++referrerCvrLookupSeq.current;
    setReferrerCvrLookupLoading(true);

    (async () => {
      try {
        const res = await api(`/api/leads/lookup/cvr/${digits}`);
        if (lookupId !== referrerCvrLookupSeq.current) {
          return;
        }

        if (!res.ok) {
          let errMsg = 'Kunne ikke slå CVR op';
          if (res.status === 404) {
            errMsg = 'Ingen virksomhed fundet for CVR-nummeret';
          } else if (res.status === 400) {
            errMsg = 'CVR-nummer skal bestå af 8 cifre';
          }
          setReferrerCvrSummary(null);
          setReferrerLastSuccessfulCvr(null);
          referrerForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
          message.error(errMsg);
          return;
        }

        const summary = (await res.json()) as CvrSummary;
        if (lookupId !== referrerCvrLookupSeq.current) {
          return;
        }

        setReferrerCvrSummary(summary);
        setReferrerLastSuccessfulCvr(digits);

        const updates: Record<string, unknown> = {};
        const summaryName = summary.navn ? summary.navn.trim() : '';
        const existingCompany = referrerForm.getFieldValue('company');
        if ((!existingCompany || !String(existingCompany).trim()) && summaryName) {
          updates.company = summaryName;
        }

        const summaryPhone = summary.telefon ? summary.telefon.trim() : '';
        if (summaryPhone) {
          updates.phone = summaryPhone;
        }

        const summaryAddress = buildAddressFromSummary(summary);
        if (summaryAddress) {
          updates.address = summaryAddress;
        }

        if (Object.keys(updates).length > 0) {
          referrerForm.setFieldsValue(updates);
        }
      } catch (error) {
        if (lookupId !== referrerCvrLookupSeq.current) {
          return;
        }
        console.error('CVR lookup failed', error);
        const errMsg = 'Kunne ikke slå CVR op';
        setReferrerCvrSummary(null);
        setReferrerLastSuccessfulCvr(null);
        referrerForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
        message.error(errMsg);
      } finally {
        if (lookupId === referrerCvrLookupSeq.current) {
          setReferrerCvrLookupLoading(false);
        }
      }
    })();
  }, [
    buildAddressFromSummary,
    createReferrerOpen,
    referrerForm,
    referrerCvrSummary,
    referrerLastSuccessfulCvr,
    resetReferrerModalState,
    watchedReferrerCvr
  ]);

  const backToLeads = useCallback(
    (backState?: { fromCompanySearch?: string | null }) => {
      if (backState?.fromCompanySearch) {
        const query = backState.fromCompanySearch.trim();
        setActiveView('companies');
        navigate(query ? `/?${query}` : '/', { replace: true, state: { fromCompanySearch: query } });
        return;
      }
      setActiveView('leads');
      navigate('/');
      loadLeads();
      if (isAdmin) {
        loadLabelOptions();
      }
    },
    [navigate, loadLeads, loadLabelOptions, isAdmin, setActiveView]
  );

  const closeLeadDetailModal = useCallback(() => {
    setLeadDetailModalOpen(false);
    setLeadDetailModalLeadId(null);
    setLeadDetailHideInsuranceTypes(false);
  }, []);

  const handleModalLeadUpdated = useCallback(
    async (leadId?: string | null) => {
      const targetLeadId = leadId ?? leadDetailModalLeadId;
      if (targetLeadId) {
        await refreshLeadOrFallback(targetLeadId);
      } else {
        await loadLeads();
      }
      if (isAdmin) {
        await loadLabelOptions();
      }
    },
    [isAdmin, leadDetailModalLeadId, loadLabelOptions, loadLeads, refreshLeadOrFallback]
  );

  const openLeadDetail = useCallback(
    (leadId: string) => {
      setLeadDetailHideInsuranceTypes(false);
      setLeadDetailModalLeadId(leadId);
      setLeadDetailModalOpen(true);
    },
    []
  );

  const openLeadDetailFromReferrers = useCallback(
    (leadId: string) => {
      setLeadDetailHideInsuranceTypes(true);
      setLeadDetailModalLeadId(leadId);
      setLeadDetailModalOpen(true);
    },
    []
  );

  const navigateToOverview = useCallback(
    (view: ActiveView) => {
      setActiveView(view);
      if (view !== 'companies') {
        setPendingWorkListId(null);
      }
      navigate('/');
      if (view === 'leads') {
        loadLeads();
        if (isAdmin) {
          loadLabelOptions();
        }
      } else if (view === 'dashboard') {
        loadLeads();
      } else if (view === 'calendar') {
        loadLeads();
      } else if (view === 'referrers') {
        loadReferrers();
      }
    },
    [navigate, loadLeads, loadReferrers, loadLabelOptions, isAdmin]
  );

  const openCompanyFromDashboardAlert = useCallback((cvrnummer: string) => {
    const cvr = cvrnummer.replace(/\D+/g, '');
    if (cvr.length !== 8) {
      message.error('Alarmens CVR-nummer er ugyldigt');
      return;
    }
    setPendingWorkListId(null);
    setActiveView('companies');
    navigate(`/?view=companies&cvr=${encodeURIComponent(cvr)}&detailCvr=${encodeURIComponent(cvr)}`);
  }, [navigate]);

  const openLeadTierOverview = useCallback(
    (tier: LeadTier) => {
      setLeadTierFilter(tier);
      navigateToOverview('leads');
    },
    [navigateToOverview]
  );

  const handleCompanySearch = useCallback(
    (rawValue: string) => {
      const value = rawValue.trim();

      if (companyLookupTimer.current) {
        clearTimeout(companyLookupTimer.current);
        companyLookupTimer.current = null;
      }

      if (!value) {
        companyLookupSeq.current += 1;
        setCompanyLookupOptions([]);
        setCompanyLookupLoading(false);
        return;
      }

      if (value.length < 3) {
        companyLookupSeq.current += 1;
        setCompanyLookupOptions([]);
        setCompanyLookupLoading(false);
        return;
      }

      setCompanyLookupLoading(true);
      const lookupId = ++companyLookupSeq.current;

      companyLookupTimer.current = setTimeout(async () => {
        try {
          const res = await api(`/api/leads/lookup/company?q=${encodeURIComponent(value)}`);
          if (lookupId !== companyLookupSeq.current) {
            return;
          }

          if (!res.ok) {
            if (res.status !== 400) {
              message.error('Kunne ikke hente virksomheder');
            }
            setCompanyLookupOptions([]);
            return;
          }

          const data = (await res.json()) as CompanyLookupResult[];
          if (lookupId !== companyLookupSeq.current) {
            return;
          }

          setCompanyLookupOptions(mapCompanyResultsToOptions(data));
        } catch (error) {
          if (lookupId !== companyLookupSeq.current) {
            return;
          }
          console.error('Company lookup failed', error);
          message.error('Kunne ikke hente virksomheder');
          setCompanyLookupOptions([]);
        } finally {
          if (lookupId === companyLookupSeq.current) {
            setCompanyLookupLoading(false);
          }
          if (companyLookupTimer.current) {
            clearTimeout(companyLookupTimer.current);
            companyLookupTimer.current = null;
          }
        }
      }, 250);
    },
    [mapCompanyResultsToOptions]
  );

  const handleCompanySelect = useCallback(
    (value: string, option: any) => {
      const updates: Record<string, unknown> = { company_name: value };
      if (option?.cvrnummer) {
        updates.cvrnummer = option.cvrnummer;
        setLastSuccessfulCvr(null);
        setCreateCvrSummary(null);
      }
      leadForm.setFieldsValue(updates);
    },
    [leadForm, setCreateCvrSummary, setLastSuccessfulCvr]
  );

  const handleReferrerCompanySearch = useCallback(
    (rawValue: string) => {
      const value = rawValue.trim();

      if (referrerCompanyTimer.current) {
        clearTimeout(referrerCompanyTimer.current);
        referrerCompanyTimer.current = null;
      }

      if (!value) {
        referrerCompanyLookupSeq.current += 1;
        setReferrerCompanyOptions([]);
        setReferrerCompanyLookupLoading(false);
        return;
      }

      if (value.length < 3) {
        referrerCompanyLookupSeq.current += 1;
        setReferrerCompanyOptions([]);
        setReferrerCompanyLookupLoading(false);
        return;
      }

      setReferrerCompanyLookupLoading(true);
      const lookupId = ++referrerCompanyLookupSeq.current;

      referrerCompanyTimer.current = setTimeout(async () => {
        try {
          const res = await api(`/api/leads/lookup/company?q=${encodeURIComponent(value)}`);
          if (lookupId !== referrerCompanyLookupSeq.current) {
            return;
          }

          if (!res.ok) {
            if (res.status !== 400) {
              message.error('Kunne ikke hente virksomheder');
            }
            setReferrerCompanyOptions([]);
            return;
          }

          const data = (await res.json()) as CompanyLookupResult[];
          if (lookupId !== referrerCompanyLookupSeq.current) {
            return;
          }

          setReferrerCompanyOptions(mapCompanyResultsToOptions(data));
        } catch (error) {
          if (lookupId !== referrerCompanyLookupSeq.current) {
            return;
          }
          console.error('Company lookup failed', error);
          message.error('Kunne ikke hente virksomheder');
          setReferrerCompanyOptions([]);
        } finally {
          if (lookupId === referrerCompanyLookupSeq.current) {
            setReferrerCompanyLookupLoading(false);
          }
          if (referrerCompanyTimer.current) {
            clearTimeout(referrerCompanyTimer.current);
            referrerCompanyTimer.current = null;
          }
        }
      }, 250);
    },
    [mapCompanyResultsToOptions]
  );

  const handleReferrerCompanySelect = useCallback(
    (value: string, option: any) => {
      const updates: Record<string, unknown> = { company: value };
      if (option?.cvrnummer) {
        updates.cvrnummer = option.cvrnummer;
        setReferrerLastSuccessfulCvr(null);
        setReferrerCvrSummary(null);
      }
      referrerForm.setFieldsValue(updates);
      referrerForm.setFields([{ name: 'cvrnummer', errors: [] }]);
    },
    [referrerForm, setReferrerCvrSummary, setReferrerLastSuccessfulCvr]
  );

  const closeCreateModal = useCallback(() => {
    setCreateOpen(false);
    setCreateCvrSummary(null);
    setCreateCvrLookupLoading(false);
    setLastSuccessfulCvr(null);
    createCvrLookupSeq.current += 1;
    setCompanyLookupOptions([]);
    setCompanyLookupLoading(false);
    if (companyLookupTimer.current) {
      clearTimeout(companyLookupTimer.current);
      companyLookupTimer.current = null;
    }
    companyLookupSeq.current += 1;
    leadForm.resetFields();
  }, [
    leadForm,
    setCompanyLookupLoading,
    setCompanyLookupOptions,
    setCreateCvrLookupLoading,
    setCreateCvrSummary,
    setLastSuccessfulCvr
  ]);

  const openCreateModal = () => {
    leadForm.resetFields();
    if (isAdmin && !referrers.length) {
      loadReferrers();
    }
    setCreateCvrSummary(null);
    setCreateCvrLookupLoading(false);
    setLastSuccessfulCvr(null);
    createCvrLookupSeq.current += 1;
    setCompanyLookupOptions([]);
    setCompanyLookupLoading(false);
    if (companyLookupTimer.current) {
      clearTimeout(companyLookupTimer.current);
      companyLookupTimer.current = null;
    }
    companyLookupSeq.current += 1;
    setCreateOpen(true);
  };

  const openCreateReferrerModal = () => {
    referrerForm.resetFields();
    resetReferrerModalState();
    setCreateReferrerOpen(true);
  };

  const closeCreateReferrerModal = useCallback(() => {
    setCreateReferrerOpen(false);
    referrerForm.resetFields();
  }, [referrerForm]);

  const openFinancialModal = (lead: any) => {
    setFinancialLead(lead);
    financialForm.setFieldsValue({
      contract_value_annual:
        lead.contract_value_annual !== null && lead.contract_value_annual !== undefined
          ? Number(lead.contract_value_annual)
          : null,
      commission_percent:
        lead.commission_percent !== null && lead.commission_percent !== undefined
          ? Number(lead.commission_percent)
          : 10
    });
    setFinancialModalOpen(true);
  };

  const closeFinancialModal = () => {
    setFinancialModalOpen(false);
    setFinancialLead(null);
    financialForm.resetFields();
  };

  const handleUpdateFinancials = async () => {
    if (!financialLead) {
      return;
    }
    try {
      const values = await financialForm.validateFields();
      const payload: Record<string, unknown> = {
        contract_value_annual:
          values.contract_value_annual !== undefined && values.contract_value_annual !== null && values.contract_value_annual !== ''
            ? Number(values.contract_value_annual)
            : null,
        commission_percent:
          values.commission_percent !== undefined && values.commission_percent !== null && values.commission_percent !== ''
            ? Number(values.commission_percent)
            : null
      };

      setFinancialModalLoading(true);
      const res = await api(`/api/leads/${financialLead.id}/value`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere økonomi');
      }

      message.success('Økonomi opdateret');
      closeFinancialModal();
      await loadLeads();
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      message.error(err?.message || 'Kunne ikke opdatere økonomi');
    } finally {
      setFinancialModalLoading(false);
    }
  };

  const handleLogout = useCallback(
    (options?: { reason?: 'expired'; message?: string }) => {
      if (options?.reason === 'expired') {
        sessionExpiredRef.current = true;
      } else {
        sessionExpiredRef.current = false;
      }

      navigate('/');
      clearTokens();
      setUser(null);

      try {
        window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      } catch (storageError) {
        console.warn('Kunne ikke rydde login-token', storageError);
      }

      setLeads([]);
      setReferrers([]);
  setLabelOptions([]);
  setSelectedLabelIds([]);
      setCreateOpen(false);
      setCreateReferrerOpen(false);
      setFinancialModalOpen(false);
      setFinancialLead(null);
      setActiveView('leads');
      leadForm.resetFields();
      referrerForm.resetFields();
      financialForm.resetFields();

      if (options?.reason === 'expired') {
        message.warning(options?.message ?? 'Din session er udløbet. Log ind igen.');
      } else if (options?.message) {
        message.info(options.message);
      } else {
        message.success('Du er logget ud');
      }
    },
    [
  navigate,
      setUser,
      setLeads,
      setReferrers,
      setCreateOpen,
      setCreateReferrerOpen,
      setFinancialModalOpen,
      setFinancialLead,
      setActiveView,
      leadForm,
      referrerForm,
      financialForm
    ]
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (sessionExpiredRef.current) {
        return;
      }
      sessionExpiredRef.current = true;
      handleLogout({ reason: 'expired' });
    });

    return () => {
      setUnauthorizedHandler(null);
    };
  }, [handleLogout]);

  useEffect(() => {
    setTokenListener((newAccess, newRefresh) => {
      try {
        if (newAccess) {
          window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newAccess);
        } else {
          window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        }

        if (newRefresh) {
          window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, newRefresh);
        } else {
          window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        }
      } catch (storageError) {
        console.warn('Kunne ikke opdatere token-lagring', storageError);
      }
    });

    return () => {
      setTokenListener(null);
    };
  }, []);

  const handleCreate = async () => {
    try {
      const values = await leadForm.validateFields();
      const payload: Record<string, unknown> = {
        company_name: values.company_name?.trim(),
        contact_name: values.contact_name ? values.contact_name.trim() : null,
        contact_email: values.contact_email ? values.contact_email.trim() : null,
        contact_phone: values.contact_phone ? values.contact_phone.trim() : null,
        notes: values.notes ? values.notes.trim() : null
      };
      const sanitizedCvr = typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      if (sanitizedCvr && sanitizedCvr.length !== 8) {
        return message.error('CVR-nummer skal bestå af 8 cifre');
      }
      if ('cvrnummer' in values) {
        payload.cvrnummer = sanitizedCvr ? sanitizedCvr : null;
      }
      if (isAdmin) {
        const selectedCurrency =
          typeof values.currency === 'string' ? values.currency.toUpperCase() : 'DKK';
        payload.contract_value_annual =
          values.contract_value_annual !== undefined && values.contract_value_annual !== null
            ? Number(values.contract_value_annual)
            : null;
        payload.commission_percent =
          values.commission_percent !== undefined && values.commission_percent !== null
            ? Number(values.commission_percent)
            : 10;
        payload.currency = selectedCurrency;
        payload.referrer_id =
          typeof values.referrer_id === 'string' ? values.referrer_id.trim() : values.referrer_id;
      }

      setCreateLoading(true);
      const res = await api('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette lead');
      }

      message.success('Lead oprettet');
      closeCreateModal();
      await loadLeads();
      if (isAdmin) {
        await loadReferrers();
      }
    } catch (err: any) {
      if (err?.errorFields) {
        return; // validation error already shown by Form
      }
      message.error(err.message || 'Kunne ikke oprette lead');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateReferrer = async () => {
    try {
      const values = await referrerForm.validateFields();
      const normalizedCvrRaw =
        typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      const normalizedCvr = normalizedCvrRaw.length === 8 ? normalizedCvrRaw : '';

      const payload: Record<string, unknown> = {
        full_name: typeof values.full_name === 'string' ? values.full_name.trim() : '',
        email: typeof values.email === 'string' ? values.email.trim().toLowerCase() : '',
        password:
          typeof values.password === 'string' && values.password.trim()
            ? values.password.trim()
            : undefined,
        phone:
          typeof values.phone === 'string' && values.phone.trim() ? values.phone.trim() : null,
        company:
          typeof values.company === 'string' && values.company.trim() ? values.company.trim() : null,
        address:
          typeof values.address === 'string' && values.address.trim() ? values.address.trim() : null,
        notes:
          typeof values.notes === 'string' && values.notes.trim() ? values.notes.trim() : null,
        preferred_language:
          typeof values.preferred_language === 'string' && values.preferred_language
            ? values.preferred_language.toLowerCase()
            : null,
        cvrnummer: normalizedCvr ? normalizedCvr : null
      };

      setCreateReferrerLoading(true);
      const res = await api('/api/referrers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette referrer');
      }

      const data = await res.json();
      setCreateReferrerOpen(false);
      referrerForm.resetFields();
      message.success('Referrer oprettet');
      if (data?.temporary_password) {
        Modal.success({
          title: 'Midlertidig adgangskode',
          content: `Del denne adgangskode med referreren: ${data.temporary_password}`,
          centered: true
        });
      }
      await loadReferrers();
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      message.error(err?.message || 'Kunne ikke oprette referrer');
    } finally {
      setCreateReferrerLoading(false);
    }
  };

  const earned = leads.reduce((sum, lead) => sum + Number(lead.commission_earned || 0), 0);
  const roleLabel = user.role === 'BROKER_ADMIN' ? 'Administrator' : 'Referrer';
  const adminMenuLabel = (label: string, description: string) => (
    <Space size={8}>
      <span>{label}</span>
      <Tooltip title={description}>
        <InfoCircleOutlined className="app-menu-info" />
      </Tooltip>
    </Space>
  );

  const adminMenuItems: MenuProps['items'] | undefined = isAdmin
    ? [
        {
          key: 'referrers',
          label: adminMenuLabel('Referrers', 'Opret og administrer de referrere, der sender leads ind.'),
          onClick: () => navigateToOverview('referrers')
        },
        {
          key: 'groups',
          label: adminMenuLabel('Grupper', 'Organiser brugere og referrere i grupper.'),
          onClick: () => navigateToOverview('groups')
        },
        {
          key: 'users',
          label: adminMenuLabel('Brugere', 'Administrer brugere, roller og adgang til portalen.'),
          onClick: () => navigateToOverview('users')
        },
        {
          key: 'change-requests',
          label: adminMenuLabel('Ændringsønsker', 'Behandl indsendte ønsker om ændringer fra brugerne.'),
          onClick: () => navigateToOverview('changeRequests')
        },
        {
          key: 'datafordeler-events',
          label: adminMenuLabel('Datafordeler', 'Se og følg op på hændelser fra Datafordeleren.'),
          onClick: () => navigateToOverview('datafordelerEvents')
        },
        {
          key: 'document-learning',
          label: adminMenuLabel('Dokumentlæring', 'Styr dokumentprofiler, dækningsmappinger og kvalitetssignaler.'),
          onClick: () => navigateToOverview('documentLearning')
        },
        {
          key: 'customization',
          label: adminMenuLabel('Tilpasning', 'Tilpas portalens fælles indstillinger og visning.'),
          onClick: () => navigateToOverview('customization')
        },
        {
          key: 'insurance-types',
          label: adminMenuLabel('Forsikringstyper', 'Administrer de forsikringstyper, der kan bruges i sager.'),
          onClick: () => navigateToOverview('insuranceTypes')
        },
        {
          key: 'insurance-conditions',
          label: adminMenuLabel('Betingelser', 'Administrer forsikringsbetingelser og deres strukturer.'),
          onClick: () => navigateToOverview('insuranceConditions')
        },
        {
          key: 'tender-templates',
          label: adminMenuLabel('Udbudsskabeloner', 'Administrer skabeloner til udbud og tilbud.'),
          onClick: () => navigateToOverview('tenderTemplates')
        },
        { type: 'divider' },
        {
          key: 'create-referrer',
          label: adminMenuLabel('Opret referrer', 'Opret en ny referrer med login og kontaktoplysninger.'),
          onClick: () => openCreateReferrerModal()
        }
      ]
    : undefined;

  const adminMenuActive =
    activeView === 'referrers' ||
    activeView === 'groups' ||
    activeView === 'users' ||
    activeView === 'datafordelerEvents' ||
    activeView === 'documentLearning' ||
    activeView === 'customization' ||
    activeView === 'changeRequests' ||
    activeView === 'insuranceTypes' ||
    activeView === 'insuranceConditions' ||
    activeView === 'tenderTemplates';

  return (
    <Layout className={`app-shell${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`} style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <aside className={`app-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}>
        <div className="app-brand"><span className="app-brand-text">Mæglerportalen</span><span className="app-brand-mark">M</span></div>
        <Button
          type="text"
          className="app-sidebar-toggle"
          icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          aria-label={sidebarCollapsed ? 'Åbn sidepanel' : 'Luk sidepanel'}
          title={sidebarCollapsed ? 'Åbn sidepanel' : 'Luk sidepanel'}
        />
        <Button
          type="primary"
          block
          icon={<PlusOutlined />}
          className="app-create-button"
          onClick={openCreateModal}
          title="Opret lead"
        >
          Opret lead
        </Button>
        <nav className="app-nav" aria-label="Hovednavigation">
          {isAdmin && (
            <div className="app-nav-group">
              <div className="app-nav-label">Overblik</div>
              <Button
                type={activeView === 'dashboard' ? 'primary' : 'text'}
                block
                icon={<DashboardOutlined />}
                className="app-nav-item"
                onClick={() => navigateToOverview('dashboard')}
                title="Se dagens vigtigste overblik, opgaver og senest opdaterede leads"
              >
                Dashboard <Tooltip title="Se dagens vigtigste overblik, opgaver og senest opdaterede leads."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
              </Button>
            </div>
          )}
          <div className="app-nav-group">
            <div className="app-nav-label">Arbejde</div>
            <Button
              type={activeView === 'leads' ? 'primary' : 'text'}
              block
              icon={<UnorderedListOutlined />}
              className="app-nav-item"
              onClick={() => navigateToOverview('leads')}
              title="Find, filtrer og behandl alle leads i salgsforløbet"
            >
              Leads <Tooltip title="Find, filtrer og behandl alle leads i salgsforløbet."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
            </Button>
            {(isBroker || isAdmin) && (
              <Button
                type={activeView === 'workLists' ? 'primary' : 'text'}
                block
                icon={<UnorderedListOutlined />}
                className="app-nav-item"
                onClick={() => navigateToOverview('workLists')}
                title="Se og slet gemte arbejdslister"
              >
                Arbejdslister
              </Button>
            )}
            {isAdmin && (
              <>
                <Button
                  type={activeView === 'todos' ? 'primary' : 'text'}
                  block
                  icon={<CheckSquareOutlined />}
                  className="app-nav-item"
                  onClick={() => navigateToOverview('todos')}
                  title="Se og følg op på opgaver, deadlines og næste handlinger"
                >
                  Todos <Tooltip title="Se og følg op på opgaver, deadlines og næste handlinger."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
                </Button>
                <Button
                  type={activeView === 'calendar' ? 'primary' : 'text'}
                  block
                  icon={<CalendarOutlined />}
                  className="app-nav-item"
                  onClick={() => navigateToOverview('calendar')}
                  title="Planlæg og få overblik over møder og aftaler"
                >
                  Kalender <Tooltip title="Planlæg og få overblik over møder og aftaler."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
                </Button>
              </>
            )}
          </div>
          {isAdmin && (
            <>
              <div className="app-nav-group">
                <div className="app-nav-label">Søgning</div>
                <Button
                  type={activeView === 'companies' ? 'primary' : 'text'}
                  block
                  icon={<SearchOutlined />}
                  className="app-nav-item"
                  onClick={() => navigateToOverview('companies')}
                  title="Søg efter nye virksomheder og opret leads fra søgeresultaterne"
                >
                  Find virksomheder <Tooltip title="Søg efter nye virksomheder og opret leads fra søgeresultaterne."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
                </Button>
              </div>
              <div className="app-nav-group">
                <div className="app-nav-label">Relationer</div>
                <Button
                  type={activeView === 'customers' ? 'primary' : 'text'}
                  block
                  icon={<TeamOutlined />}
                  className="app-nav-item"
                  onClick={() => navigateToOverview('customers')}
                  title="Se og administrer aktive kunder og deres aftaler"
                >
                  Kunder <Tooltip title="Se og administrer aktive kunder og deres aftaler."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
                </Button>
                <Button
                  type={activeView === 'leadGroups' ? 'primary' : 'text'}
                  block
                  icon={<TeamOutlined />}
                  className="app-nav-item"
                  onClick={() => navigateToOverview('leadGroups')}
                  title="Saml og håndter kunder, der hører sammen i samme relation"
                >
                  Samlekunder <Tooltip title="Saml og håndter kunder, der hører sammen i samme relation."><InfoCircleOutlined className="app-nav-info" /></Tooltip>
                </Button>
              </div>
            </>
          )}
          {isAdmin && adminMenuItems && (
            <div className="app-nav-group">
              <div className="app-nav-label">Administration</div>
              <Dropdown menu={{ items: adminMenuItems }} trigger={['click']}>
                <Button
                  type={adminMenuActive ? 'primary' : 'text'}
                  block
                  className="app-nav-item app-nav-item-dropdown"
                  icon={<SettingOutlined />}
                  title="Administration"
                >
                  <span>Administration</span><Tooltip title="Administrer brugere, grupper, indstillinger og systemdata."><InfoCircleOutlined className="app-nav-info" /></Tooltip><DownOutlined />
                </Button>
              </Dropdown>
            </div>
          )}
        </nav>
      </aside>
      <Layout.Header
        className="app-header"
      >
        <Row style={{ width: '100%' }} align="middle" justify="space-between">
          <Col>
            {onDetailPage && (
              <Button type="link" onClick={() => navigateToOverview('leads')}>
                Tilbage til leads
              </Button>
            )}
          </Col>
          <Col>
            <Space size="middle" align="center">
              <Tag color="geekblue">{roleLabel}</Tag>
              <span className="app-user-email">{user.email}</span>
              <Button onClick={() => handleLogout()}>Log ud</Button>
            </Space>
          </Col>
        </Row>
      </Layout.Header>

      <Layout.Content className="app-content">
        <Routes>
          <Route
            path="/"
            element={
              activeView === 'dashboard' ? (
                <DashboardPage
                  earned={earned}
                  activeLeadCount={activeLeadCount}
                  tierCounts={tierCounts}
                  recentLeads={dashboardRecentLeads}
                  recentLeadsLoading={dashboardRecentLeadsLoading}
                  propertyPortfolios={dashboardPropertyPortfolios}
                  propertyPortfoliosLoading={dashboardPropertyPortfoliosLoading}
                  propertyPortfoliosError={dashboardPropertyPortfoliosError}
                  propertyAlerts={dashboardPropertyAlerts}
                  propertyAlertsLoading={dashboardPropertyAlertsLoading}
                  includeHiddenRecentLeads={dashboardIncludeHiddenRecentLeads}
                  upcomingMeetings={upcomingMeetings}
                  upcomingMeetingsLoading={upcomingMeetingsLoading}
                  meetingWindowDays={UPCOMING_MEETING_WINDOW_DAYS}
                  upcomingTodos={upcomingTodos}
                  upcomingTodosLoading={upcomingTodosLoading}
                  todoWindowDays={UPCOMING_TODO_WINDOW_DAYS}
                  canManageTodos={isAdmin}
                  showPipelineHistory={isAdmin}
                  pipelineHistory={pipelineHistoryData}
                  pipelineHistoryLoading={pipelineHistoryLoading}
                  pipelineHistoryError={pipelineHistoryError}
                  pipelineHistoryWindow={pipelineHistoryWindow}
                  onPipelineWindowChange={handlePipelineWindowChange}
                  onIncludeHiddenRecentLeadsChange={handleDashboardIncludeHiddenRecentLeadsChange}
                  onOpenLead={openLeadDetail}
				  onOpenCompany={openCompanyFromDashboardAlert}
                  onGoToCalendar={() => navigateToOverview('calendar')}
                  onGoToTodos={() => navigateToOverview('todos')}
                  onOpenLeadTier={openLeadTierOverview}
                />
              ) : activeView === 'leads' ? (
                <>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                      <Statistic
                        title="Samlet provision"
                        value={earned}
                        formatter={(value) => formatAmount(value as number, 'DKK')}
                      />
                    </Col>
                    <Col>
                      <Statistic
                        title="Pipeline"
                        value={activeLeadCount}
                      />
                    </Col>
                    <Col>
                      <Statistic title="Leads" value={tierCounts.LEAD} />
                    </Col>
                    <Col>
                      <Statistic title="Sager" value={tierCounts.IN_PROGRESS} />
                    </Col>
                    <Col>
                      <Statistic title="Kunder" value={tierCounts.CUSTOMER} />
                    </Col>
                  </Row>


                  <Row
                    style={{ marginBottom: 16 }}
                    gutter={[16, 16]}
                    justify={isAdmin ? 'space-between' : 'end'}
                    align="middle"
                  >
                    {isAdmin && (
                      <Col flex="1 1 240px">
                        <Select
                          mode="multiple"
                          allowClear
                          placeholder="Filtrer efter labels"
                          value={selectedLabelIds}
                          onChange={handleLabelFilterChange}
                          options={labelOptions.map((label) => ({ value: label.id, label: label.name }))}
                          optionFilterProp="label"
                          maxTagCount="responsive"
                          style={{ minWidth: 240, width: '100%' }}
                        />
                      </Col>
                    )}
                    {isAdmin && (
                      <Col flex="0 0 auto">
                        <Checkbox checked={includeHiddenLeads} onChange={handleHiddenToggle}>
                          Vis skjulte leads
                        </Checkbox>
                      </Col>
                    )}
                    {isAdmin && (
                      <Col flex="0 0 auto">
                        <Checkbox checked={showMonitoredLeads} onChange={handleMonitoredToggle}>
                          Vis overvågede
                        </Checkbox>
                      </Col>
                    )}
                    <Col flex="0 0 auto">
                      <Segmented
                        value={leadTierFilter}
                        onChange={(value) => handleTierFilterChange(value as 'ALL' | LeadTier)}
                        options={[
                          { value: 'ALL', label: 'Alle' },
                          ...leadTierDefinitions.map((definition) => ({
                            value: definition.value,
                            label: definition.value === 'IN_PROGRESS' ? 'Sager' : definition.label
                          }))
                        ]}
                      />
                    </Col>
                    <Col flex="0 0 320px">
                      <Input.Search
                        placeholder="Søg i pipeline"
                        allowClear
                        value={searchInput}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        enterButton="Søg"
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    {visibleLeads.map((lead) => (
                      <Col span={24} key={lead.id}>
                        <LeadCard
                          lead={lead}
                          refresh={() => refreshLeadOrFallback(lead.id)}
                          canManage={isAdmin}
                          onEditFinancials={isAdmin ? openFinancialModal : undefined}
                          onOpenDetail={openLeadDetail}
                          onAgreementRequest={openAgreementWizard}
                          availableLabels={isAdmin ? labelOptions : undefined}
                          onAttachLabels={isAdmin ? attachLabelsToLead : undefined}
                        />
                      </Col>
                    ))}
                  </Row>
                  {leadListHasMore ? (
                    <Space style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
                      <Button
                        onClick={() => void loadLeads({ offset: leads.length, append: true })}
                        loading={leadListLoading}
                      >
                        Hent flere leads
                      </Button>
                    </Space>
                  ) : null}
                </>
              ) : activeView === 'todos' ? (
                <LeadTodosPage availableLeads={leads} onOpenLead={openLeadDetail} />
              ) : activeView === 'calendar' ? (
                <CalendarPage availableLeads={leads} onOpenLead={openLeadDetail} />
              ) : activeView === 'referrers' ? (
                <AdminReferrersPage onOpenLead={openLeadDetailFromReferrers} />
              ) : activeView === 'groups' ? (
                <AdminGroupsPage />
              ) : activeView === 'users' ? (
                <AdminUsersPage />
              ) : activeView === 'changeRequests' ? (
                <ChangeRequestsPage />
              ) : activeView === 'datafordelerEvents' ? (
                <AdminDatafordelerEventsPage />
              ) : activeView === 'documentLearning' ? (
                <DocumentLearningPage />
              ) : activeView === 'customization' ? (
                <CustomizationPage />
              ) : activeView === 'insuranceTypes' ? (
                <InsuranceTypesPage />
              ) : activeView === 'insuranceConditions' ? (
                <InsuranceConditionsPage />
              ) : activeView === 'tenderTemplates' ? (
                <TenderTemplatesPage />
              ) : activeView === 'customers' ? (
                <CustomersPage />
              ) : activeView === 'leadGroups' ? (
                <LeadGroupsPage
                  canManage={isAdmin}
                  onOpenLead={openLeadDetail}
                  onAgreementRequest={openAgreementWizard}
                />
              ) : activeView === 'companies' ? (
                <CompanySearchPage
                  canManage={isAdmin}
                  initialWorkListId={pendingWorkListId}
                  onLeadCreated={loadLeads}
                />
              ) : activeView === 'workLists' && (isBroker || isAdmin) ? (
                <WorkListsManagementPage />
              ) : (
                <CompanySearchPage
                  canManage={isAdmin}
                  initialWorkListId={pendingWorkListId}
                  onLeadCreated={loadLeads}
                />
              )
            }
          />
          <Route
            path="/leads/:leadId"
            element={
              <LeadDetailPage
                canManage={isAdmin}
                onBack={backToLeads}
                onLeadUpdated={refreshLeadOrFallback}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout.Content>

      <Modal
        title="Lead-detaljer"
        open={leadDetailModalOpen}
        onCancel={closeLeadDetailModal}
        footer={null}
        width="92vw"
        style={{ maxWidth: 1600, top: 24 }}
        destroyOnClose
      >
        {leadDetailModalLeadId ? (
          <LeadDetailPage
            key={leadDetailModalLeadId}
            canManage={isAdmin}
            onBack={closeLeadDetailModal}
            onLeadUpdated={handleModalLeadUpdated}
            hideInsuranceTypes={leadDetailHideInsuranceTypes}
            leadIdOverride={leadDetailModalLeadId}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        )}
      </Modal>

      <Modal
        title="Opret referrer"
        open={createReferrerOpen}
        onCancel={closeCreateReferrerModal}
        onOk={handleCreateReferrer}
        confirmLoading={createReferrerLoading}
        destroyOnClose
      >
        <Form form={referrerForm} layout="vertical" preserve={false} autoComplete="off">
          <Form.Item
            name="cvrnummer"
            label="CVR-nummer"
            rules={[{ validator: validateOptionalCvr }]}
            extra={
              referrerCvrLookupLoading ? (
                <span style={{ color: '#8c8c8c' }}>Henter CVR-oplysninger...</span>
              ) : referrerCvrSummary ? (
                <div style={{ color: '#8c8c8c' }}>
                  <div>{referrerCvrSummary.navn || 'Virksomhedsnavn ikke fundet'}</div>
                  {(referrerCvrSummary.postnummer || referrerCvrSummary.postdistrikt) && (
                    <div>
                      {[referrerCvrSummary.postnummer, referrerCvrSummary.postdistrikt]
                        .filter(Boolean)
                        .join(' ')}
                    </div>
                  )}
                  {(referrerCvrSummary.telefon || referrerCvrSummary.email) && (
                    <div>
                      {referrerCvrSummary.telefon ? `Tlf.: ${referrerCvrSummary.telefon}` : null}
                      {referrerCvrSummary.telefon && referrerCvrSummary.email ? ' · ' : null}
                      {referrerCvrSummary.email ? `E-mail: ${referrerCvrSummary.email}` : null}
                    </div>
                  )}
                </div>
              ) : undefined
            }
          >
            <Input placeholder="F.eks. 12345678" maxLength={20} />
          </Form.Item>
          <Form.Item name="company" label="Virksomhedsnavn">
            <AutoComplete
              options={referrerCompanyOptions}
              onSearch={handleReferrerCompanySearch}
              onSelect={handleReferrerCompanySelect}
              onClear={() => {
                setReferrerCompanyOptions([]);
              }}
              filterOption={false}
              allowClear
              notFoundContent={referrerCompanyLookupLoading ? 'Søger...' : 'Ingen resultater'}
            >
              <Input placeholder="Virksomhedsnavn" />
            </AutoComplete>
          </Form.Item>
          <Form.Item
            name="full_name"
            label="Navn"
            rules={[{ required: true, message: 'Angiv referrerens navn' }]}
          >
            <Input placeholder="Fulde navn" />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              { required: true, message: 'Angiv en e-mail' },
              { type: 'email', message: 'Angiv en gyldig e-mail' }
            ]}
          >
            <Input placeholder="referrer@firma.dk" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Initial adgangskode"
            tooltip="Hvis feltet er tomt genereres en midlertidig adgangskode automatisk"
          >
            <Input.Password placeholder="Lad stå tom for automatisk kode" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input placeholder="F.eks. +45 12 34 56 78" />
          </Form.Item>
          <Form.Item name="address" label="Adresse">
            <Input placeholder="Adresse" />
          </Form.Item>
          <Form.Item name="preferred_language" label="Foretrukket sprog">
            <Select allowClear options={languageOptions} placeholder="Vælg sprog" />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} placeholder="Evt. interne noter" />
          </Form.Item>
        </Form>
      </Modal>

      <AgreementModal
        open={agreementModalOpen}
        loading={agreementModalLoading}
        submitting={agreementSubmitting}
        initialValues={agreementInitialValues}
        insuranceTypes={[]}
        availableInsuranceTypes={agreementAvailableInsuranceTypes}
        selectedInsuranceTypeIds={agreementSelectedInsuranceTypeIds}
        onToggleInsuranceType={(id, checked) => {
          setAgreementSelectedInsuranceTypeIds((prev) => {
            const next = new Set(prev);
            if (checked) {
              next.add(id);
            } else {
              next.delete(id);
            }
            return Array.from(next);
          });
        }}
        onCancel={() => {
          setAgreementModalOpen(false);
          setAgreementSelectedInsuranceTypeIds([]);
        }}
        onSubmit={handleAgreementSubmit}
        onSaveDraft={handleAgreementSaveDraft}
        saveDraftLoading={agreementSavingDraft}
      />

      <Modal
        title="Opret lead"
        open={createOpen}
        onCancel={closeCreateModal}
        onOk={handleCreate}
        confirmLoading={createLoading}
        destroyOnClose
      >
        <Form
          form={leadForm}
          layout="vertical"
          preserve={false}
          initialValues={{ currency: 'DKK', commission_percent: 10 }}
        >
          {isAdmin && (
            <Form.Item
              name="referrer_id"
              label="Referrer"
              rules={[{ required: true, message: 'Vælg en referrer' }]}
            >
              <Select
                placeholder="Vælg referrer"
                options={(() => {
                  const options = referrers.map((r) => ({ value: r.id, label: r.label }));
                  if (user) {
                    const userEmail = user.email.toLowerCase();
                    const hasSelf = referrers.some(
                      (r) => typeof r.email === 'string' && r.email.toLowerCase() === userEmail
                    );
                    if (!hasSelf) {
                      options.unshift({ value: 'SELF', label: `${user.email} (mig selv)` });
                    }
                  }
                  return options;
                })()}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          )}
          <Form.Item
            name="cvrnummer"
            label="CVR-nummer"
            rules={[{ validator: validateOptionalCvr }]}
            extra={
              createCvrLookupLoading ? (
                <span style={{ color: '#8c8c8c' }}>Henter CVR-oplysninger...</span>
              ) : createCvrSummary ? (
                <div style={{ color: '#8c8c8c' }}>
                  <div>{createCvrSummary.navn || 'Virksomhedsnavn ikke fundet'}</div>
                  {(createCvrSummary.postnummer || createCvrSummary.postdistrikt) && (
                    <div>
                      {[createCvrSummary.postnummer, createCvrSummary.postdistrikt]
                        .filter(Boolean)
                        .join(' ')}
                    </div>
                  )}
                  {(createCvrSummary.telefon || createCvrSummary.email) && (
                    <div>
                      {createCvrSummary.telefon ? `Tlf.: ${createCvrSummary.telefon}` : null}
                      {createCvrSummary.telefon && createCvrSummary.email ? ' · ' : null}
                      {createCvrSummary.email ? `E-mail: ${createCvrSummary.email}` : null}
                    </div>
                  )}
                </div>
              ) : undefined
            }
          >
            <Input placeholder="F.eks. 12345678" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="company_name"
            label="Virksomhedsnavn"
            rules={[{ required: true, message: 'Angiv virksomhedsnavn' }]}
          >
            <AutoComplete
              options={companyLookupOptions}
              onSearch={handleCompanySearch}
              onSelect={handleCompanySelect}
              onClear={() => {
                setCompanyLookupOptions([]);
              }}
              filterOption={false}
              allowClear
              notFoundContent={companyLookupLoading ? 'Søger...' : 'Ingen resultater'}
            >
              <Input placeholder="Virksomhedsnavn" />
            </AutoComplete>
          </Form.Item>
          <Form.Item name="contact_name" label="Kontaktperson">
            <Input placeholder="Navn på kontaktperson" />
          </Form.Item>
          <Form.Item
            name="contact_email"
            label="Kontakt e-mail"
            rules={[{ type: 'email', message: 'Angiv en gyldig e-mail' }]}
          >
            <Input placeholder="kontakt@firma.dk" />
          </Form.Item>
          <Form.Item name="contact_phone" label="Kontakt telefon">
            <Input placeholder="F.eks. +45 12 34 56 78" />
          </Form.Item>
          <Form.Item
            name="contract_value_annual"
            label={
              <Space>
                <span>Årlig kontraktværdi</span>
                {!isAdmin && <Tag color="blue">Kun admin</Tag>}
              </Space>
            }
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="F.eks. 500000"
              disabled={!isAdmin}
            />
          </Form.Item>
          <Form.Item
            name="commission_percent"
            label={
              <Space>
                <span>Provision (%)</span>
                {!isAdmin && <Tag color="blue">Kun admin</Tag>}
              </Space>
            }
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              placeholder="F.eks. 15"
              disabled={!isAdmin}
            />
          </Form.Item>
          <Form.Item
            name="currency"
            label={
              <Space>
                <span>Valuta</span>
                {!isAdmin && <Tag color="blue">Kun admin</Tag>}
              </Space>
            }
            rules={isAdmin ? [{ required: true, message: 'Vælg en valuta' }] : []}
          >
            <Select options={currencyOptions} placeholder="Vælg valuta" disabled={!isAdmin} />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} placeholder="Evt. noter" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={financialLead ? `Opdater økonomi for ${financialLead.company_name}` : 'Opdater økonomi'}
        open={financialModalOpen}
        onCancel={closeFinancialModal}
        onOk={handleUpdateFinancials}
        confirmLoading={financialModalLoading}
        destroyOnClose
      >
        <Form form={financialForm} layout="vertical" preserve={false}>
          <Form.Item name="contract_value_annual" label="Årlig kontraktværdi">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="F.eks. 500000" />
          </Form.Item>
          <Form.Item name="commission_percent" label="Provision (%)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="F.eks. 15" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default function App() {
  const [user, setUser] = useState<Claims | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const storedAccess = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        const storedRefresh = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

        // Always prefer a server-validated refresh when available.
        // This avoids booting into the app with a stale access token.
        if (storedRefresh) {
          setTokens('', storedRefresh);
          const refreshedAccess = await refreshAccessToken();
          const claims = refreshedAccess ? decodeClaims(refreshedAccess) : decodeClaims(getAccessToken());
          if (claims) {
            if (isMounted) {
              setUser(claims);
            }
            return;
          }
        }

        if (storedAccess && !isTokenExpired(storedAccess)) {
          const claims = decodeClaims(storedAccess);
          if (claims) {
            setTokens(storedAccess, storedRefresh);
            if (isMounted) {
              setUser(claims);
            }
            return;
          }
        }

        clearTokens();
        window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      } catch (storageError) {
        console.warn('Kunne ikke læse login-token', storageError);
        try {
          clearTokens();
          window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        } catch {
          /* noop */
        }
      } finally {
        if (isMounted) {
          setBootstrapped(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!bootstrapped) {
    return null;
  }

  if (!user) {
    return <Login onOk={(claims) => setUser(claims)} />;
  }

  return (
    <BrowserRouter>
      <AppRoutes user={user} setUser={setUser} />
    </BrowserRouter>
  );
}